import React from "react";
import { isFleetPerspectiveTarget } from "../utils/fleetTimelinePerspective.js";
import { fetchReassignmentCandidates, fetchSuspendedDeliveryReactivationCandidates } from "./useReassignmentCandidates.js";
import {
  buildGroupedReassignmentDragCandidate,
  buildReassignmentDragCandidate,
  buildReassignmentVisibleTruckCapabilityIndex,
  filterEndOfDayLoadTargetTrucks,
  resolveImmediateDeliveryId,
  resolveSegmentReassignmentGroupDrag,
} from "../utils/fleetReassignmentHelpers.js";
import {
  isPlanningStateChangeInScope,
  readPlanningStateChangeScopeFromLocation,
  subscribePlanningStateChange,
} from "../utils/planningCrossViewSync.js";
import {
  isDispatchFastReassignmentMode,
  normalizeDispatchReassignmentMode,
} from "../utils/dispatchReassignmentMode.js";
import { getActivePlanningMutationCount } from "../utils/planningMutationActivity.js";

export const REASSIGNMENT_POINTER_HOLD_DELAY_MS = 1000;
export const REASSIGNMENT_POINTER_MOVE_THRESHOLD_PX = 6;

function isPlanningMutationActivityActive() {
  return getActivePlanningMutationCount() > 0;
}

export function hasReassignmentPointerMoved(
  startPosition = null,
  endPosition = null,
  moveThresholdPx = REASSIGNMENT_POINTER_MOVE_THRESHOLD_PX,
) {
  const startX = Number(startPosition?.clientX);
  const startY = Number(startPosition?.clientY);
  const endX = Number(endPosition?.clientX);
  const endY = Number(endPosition?.clientY);
  if (![startX, startY, endX, endY].every(Number.isFinite)) {
    return false;
  }

  return Math.hypot(endX - startX, endY - startY) >= moveThresholdPx;
}

function normalizeTruckIdentity(value) {
  return typeof value === "string" ? value.trim() : "";
}

function normalizeReassignmentText(value) {
  return typeof value === "string" ? value.trim() : "";
}

function normalizeReassignmentDispatchPeriod(value) {
  const normalizedValue = normalizeReassignmentText(value).toLowerCase();
  return normalizedValue === "evening" || normalizedValue === "soir" ? "evening" : "day";
}

function resolveSegmentProductLabel(segment = null) {
  return normalizeReassignmentText(segment?.productLabel)
    || normalizeReassignmentText(segment?.manualMixCurrentProductLabel)
    || normalizeReassignmentText(segment?.businessStep?.orderDetails?.grainType)
    || normalizeReassignmentText(segment?.businessStep?.grainType)
    || "";
}

function resolveReassignmentSourceProductLabel(segment = null, groupDrag = null) {
  const groupSegments = Array.isArray(groupDrag?.reassignmentSegments)
    ? groupDrag.reassignmentSegments
    : [];
  const groupProductLabel = groupSegments
    .map((groupSegment) => resolveSegmentProductLabel(groupSegment))
    .find(Boolean);

  return groupProductLabel || resolveSegmentProductLabel(segment);
}

export function resolveReassignmentSourceOrderDompeurRequirements(segment = null, groupDrag = null) {
  const groupSegments = Array.isArray(groupDrag?.reassignmentSegments)
    ? groupDrag.reassignmentSegments.filter(Boolean)
    : [];
  const sourceSegments = groupSegments.length > 0 ? groupSegments : (segment ? [segment] : []);
  if (sourceSegments.length === 0) {
    return [];
  }

  const requirementByOrderId = new Map();
  for (const [index, sourceSegment] of sourceSegments.entries()) {
    const editableOrder = sourceSegment?.businessStep?.orderDetails?.editableOrder;
    if (!editableOrder || typeof editableOrder.is_dompeur !== "boolean") {
      return [];
    }

    const orderId = normalizeReassignmentText(
      sourceSegment?.operationOrderId
      || sourceSegment?.reassignmentDrag?.orderId
      || editableOrder?.order_id
      || editableOrder?.orderId,
    ) || `segment-${index}`;
    const existingRequirement = requirementByOrderId.get(orderId);
    if (typeof existingRequirement === "boolean" && existingRequirement !== editableOrder.is_dompeur) {
      return [];
    }
    requirementByOrderId.set(orderId, editableOrder.is_dompeur);
  }

  return [...requirementByOrderId.values()];
}

export function resolveSuspendedDeliverySourceProductLabel(item = null) {
  const productLabel = normalizeReassignmentText(item?.productLabel);
  if (!productLabel) {
    return "";
  }

  const normalizedProductKey = productLabel
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();

  return normalizedProductKey === "mixte" ? "" : productLabel;
}

export function isEquivalentReassignmentTarget(reassignmentDragState = null, targetAssignmentId = "", compatibility = null) {
  if (reassignmentDragState?.dragKind !== "assignment") {
    return false;
  }

  const sourceTruckId = normalizeTruckIdentity(reassignmentDragState?.sourceAssignmentId);
  if (!sourceTruckId) {
    return false;
  }

  const normalizedTargetAssignmentId = normalizeTruckIdentity(targetAssignmentId);
  const targetTruckId = normalizeTruckIdentity(compatibility?.truck_id) || normalizedTargetAssignmentId;

  if (!targetTruckId) {
    return false;
  }

  return Boolean(
    compatibility?.is_current_truck === true
    || sourceTruckId === normalizedTargetAssignmentId
    || sourceTruckId === targetTruckId
  );
}

export function isMatchingUndoReassignment(currentState, referenceState) {
  return Boolean(
    currentState
    && referenceState
    && normalizeReassignmentText(String(currentState.operationId ?? ""))
    && normalizeReassignmentText(String(currentState.operationId ?? ""))
      === normalizeReassignmentText(String(referenceState.operationId ?? "")),
  );
}

function getTimelineRowFromClientPoint(clientX, clientY) {
  if (typeof document === "undefined") {
    return null;
  }

  const target = document.elementFromPoint(clientX, clientY);
  if (!(target instanceof Element)) {
    return null;
  }

  const rowElement = target.closest('[data-fleet-timeline-row="true"]');
  if (isFleetPerspectiveTarget(rowElement)) return null;
  if (!(rowElement instanceof HTMLElement)) {
    return null;
  }

  return rowElement;
}

function resolveReassignmentDropTargetFromClientPoint(clientX, clientY) {
  if (typeof document === "undefined") {
    return { assignmentId: "", dropZone: "" };
  }

  const target = document.elementFromPoint(clientX, clientY);
  if (!(target instanceof Element)) {
    return { assignmentId: "", dropZone: "" };
  }

  const suspendedDropZoneElement = target.closest('[data-fleet-suspended-drop-zone="true"]');
  if (suspendedDropZoneElement instanceof HTMLElement) {
    return {
      assignmentId: "",
      dispatchPeriod: normalizeReassignmentDispatchPeriod(
        suspendedDropZoneElement.dataset?.dispatchPeriod
          || suspendedDropZoneElement.getAttribute?.("data-dispatch-period"),
      ),
      dropZone: "suspended",
    };
  }

  const rowElement = getTimelineRowFromClientPoint(clientX, clientY);
  const dispatchPeriod = normalizeReassignmentText(
    rowElement?.dataset?.dispatchPeriod || rowElement?.getAttribute?.("data-dispatch-period"),
  ).toLowerCase();

  return {
    assignmentId: rowElement?.dataset?.assignmentId || rowElement?.getAttribute?.("data-assignment-id") || "",
    dispatchPeriod: normalizeReassignmentDispatchPeriod(dispatchPeriod),
    dropZone: "truck",
  };
}

export function resolveSuspendedDropZoneDispatchPeriod(event = null, explicitDispatchPeriod = "") {
  return normalizeReassignmentDispatchPeriod(
    explicitDispatchPeriod
      || event?.currentTarget?.dataset?.dispatchPeriod
      || event?.currentTarget?.getAttribute?.("data-dispatch-period"),
  );
}

export function canDropActiveDeliveryOnSuspendedPeriod(currentState = null, targetDispatchPeriod = "day") {
  return Boolean(
    currentState?.dragKind === "assignment"
    && currentState?.deliveryId
    && normalizeReassignmentDispatchPeriod(currentState.sourceDispatchPeriod)
      === normalizeReassignmentDispatchPeriod(targetDispatchPeriod),
  );
}

export function canTransferSuspendedDeliveryToPeriod(currentState = null, targetDispatchPeriod = "day") {
  return Boolean(
    currentState?.dragKind === "suspended"
    && currentState?.deliveryId
    && normalizeReassignmentDispatchPeriod(currentState.sourceDispatchPeriod)
      !== normalizeReassignmentDispatchPeriod(targetDispatchPeriod),
  );
}

export function buildSuspendedPeriodTransferDragPayload(currentState, dispatchDate, targetPeriod) {
  if (!dispatchDate || !canTransferSuspendedDeliveryToPeriod(currentState, targetPeriod)) {
    return null;
  }
  const deliveryIds = normalizePreviewDeliveryIds([
    ...(currentState.deliveryIds || []), currentState.deliveryId,
  ]);
  return {
    destinationKind: "suspended",
    deliveryId: deliveryIds[0],
    deliveryIds,
    dispatchDate,
    expectedRuntimeRevision: currentState.sourceRuntimeRevision || "",
    orderIds: normalizePreviewDeliveryIds(currentState.orderIds),
    scope: deliveryIds.length > 1 ? "group" : "delivery",
    sourceDispatchPeriod: normalizeReassignmentDispatchPeriod(currentState.sourceDispatchPeriod),
    targetPeriod: normalizeReassignmentDispatchPeriod(targetPeriod),
  };
}

function normalizePreviewDeliveryIds(value) {
  return (Array.isArray(value) ? value : [])
    .filter((deliveryId) => typeof deliveryId === "string" && deliveryId.trim())
    .map((deliveryId) => deliveryId.trim())
    .filter((deliveryId, index, items) => items.indexOf(deliveryId) === index);
}

function resolveReassignmentOrderId(item = null) {
  if (typeof item === "string") {
    return normalizeReassignmentText(item);
  }
  return normalizeReassignmentText(
    item?.order_id
      || item?.orderId
      || item?.business_order_id
      || item?.businessOrderId
      || item?.work_order_number
      || item?.workOrderNumber,
  );
}

export function resolveUndoReassignmentSourceOrderIds(row = null) {
  const assignmentOrderIds = normalizePreviewDeliveryIds(
    (Array.isArray(row?.assignment?.orders) ? row.assignment.orders : [])
      .map((item) => resolveReassignmentOrderId(item)),
  );
  if (assignmentOrderIds.length > 0) {
    return assignmentOrderIds;
  }

  return normalizePreviewDeliveryIds(
    (Array.isArray(row?.segments) ? row.segments : [])
      .slice()
      .sort((left, right) => Number(left?.globalSequence || 0) - Number(right?.globalSequence || 0))
      .map((segment) => (
        segment?.segmentReorder?.orderId
        || segment?.operationOrderId
        || segment?.orderLabel
      )),
  );
}

function resolveUndoReassignmentResultPayload(context = null) {
  const payload = context?.payload && typeof context.payload === "object"
    ? context.payload
    : null;
  if (payload?.result_payload && typeof payload.result_payload === "object") {
    return payload.result_payload;
  }
  return payload;
}

export function resolveUndoReassignmentGroupStepIds(
  resultPayload = null,
  deliveryIds = [],
  targetTruckId = "",
) {
  const expectedDeliveryIds = normalizePreviewDeliveryIds(deliveryIds);
  if (expectedDeliveryIds.length < 2 || !resultPayload || typeof resultPayload !== "object") {
    return [];
  }

  const expectedDeliveryIdSet = new Set(expectedDeliveryIds);
  const normalizedTargetTruckId = normalizeTruckIdentity(targetTruckId);
  const stepIdByDeliveryId = new Map();
  const assignments = Array.isArray(resultPayload.assignments) ? resultPayload.assignments : [];

  assignments.forEach((assignment) => {
    const assignmentTruckId = normalizeTruckIdentity(assignment?.truck_id || assignment?.truckId);
    if (normalizedTargetTruckId && assignmentTruckId !== normalizedTargetTruckId) {
      return;
    }

    const trips = Array.isArray(assignment?.trips) ? assignment.trips : [];
    trips.forEach((trip) => {
      const steps = Array.isArray(trip?.operational_steps)
        ? trip.operational_steps
        : (Array.isArray(trip?.operationalSteps) ? trip.operationalSteps : []);
      steps.forEach((step) => {
        const stepId = normalizeReassignmentText(step?.step_id || step?.stepId);
        if (!stepId) {
          return;
        }

        const stepDeliveryIds = normalizePreviewDeliveryIds([
          step?.delivery_id,
          step?.deliveryId,
          ...(Array.isArray(step?.delivery_ids) ? step.delivery_ids : []),
          ...(Array.isArray(step?.deliveryIds) ? step.deliveryIds : []),
        ]);
        stepDeliveryIds.forEach((deliveryId) => {
          if (expectedDeliveryIdSet.has(deliveryId) && !stepIdByDeliveryId.has(deliveryId)) {
            stepIdByDeliveryId.set(deliveryId, stepId);
          }
        });
      });
    });
  });

  if (expectedDeliveryIds.some((deliveryId) => !stepIdByDeliveryId.has(deliveryId))) {
    return [];
  }
  return expectedDeliveryIds.map((deliveryId) => stepIdByDeliveryId.get(deliveryId));
}

export function buildUndoReassignmentCandidate(currentState = null, {
  destinationKind = "truck",
  dispatchDate = "",
  dispatchPeriod = "day",
  targetTruckId = "",
  targetTruckName = "",
} = {}) {
  if (!currentState || !["assignment", "suspended"].includes(currentState.dragKind)) {
    return null;
  }

  const deliveryIds = normalizePreviewDeliveryIds([
    ...(Array.isArray(currentState.deliveryIds) ? currentState.deliveryIds : []),
    currentState.deliveryId,
  ]);
  const normalizedTargetTruckId = normalizeTruckIdentity(targetTruckId);
  const originKind = currentState.dragKind === "suspended" ? "suspended" : "truck";
  const normalizedDestinationKind = destinationKind === "suspended" ? "suspended" : "truck";
  const sourceTruckId = originKind === "truck"
    ? normalizeTruckIdentity(currentState.sourceAssignmentId)
    : "";
  if (
    deliveryIds.length === 0
    || (normalizedDestinationKind === "truck" && !normalizedTargetTruckId)
    || (originKind === "truck" && !sourceTruckId)
    || (originKind === "suspended" && normalizedDestinationKind === "suspended")
  ) {
    return null;
  }

  const normalizedDispatchPeriod = normalizeReassignmentDispatchPeriod(dispatchPeriod);
  const operationId = [
    normalizeReassignmentText(dispatchDate),
    normalizedDispatchPeriod,
    normalizeReassignmentText(String(currentState.requestId ?? "")),
    originKind,
    deliveryIds.join(","),
    normalizedDestinationKind,
    normalizedDestinationKind === "suspended" ? "suspended" : normalizedTargetTruckId,
  ].join("::");

  return {
    deliveryId: deliveryIds[0],
    deliveryIds,
    destinationKind: normalizedDestinationKind,
    dispatchDate: normalizeReassignmentText(dispatchDate),
    dispatchPeriod: normalizedDispatchPeriod,
    expectedRuntimeRevision: "",
    groupStepIds: [],
    isPending: true,
    operationId,
    orderIds: normalizePreviewDeliveryIds(currentState.orderIds),
    originKind,
    rebuildMode: normalizeDispatchReassignmentMode(currentState.rebuildMode),
    sourceOrderIds: normalizePreviewDeliveryIds(currentState.sourceOrderIds),
    sourceLabel: normalizeReassignmentText(currentState.sourceLabel),
    sourceTruckId,
    sourceTruckName: originKind === "suspended"
      ? "Suspendues"
      : normalizeReassignmentText(currentState.sourceTruckName) || sourceTruckId,
    targetTruckId: normalizedDestinationKind === "suspended" ? "" : normalizedTargetTruckId,
    targetTruckName: normalizedDestinationKind === "suspended"
      ? "Suspendues"
      : normalizeReassignmentText(targetTruckName) || normalizedTargetTruckId,
  };
}

export function buildUndoReassignmentGroupOrderRestore(candidate = null, context = null) {
  const sourceOrderIds = normalizePreviewDeliveryIds(candidate?.sourceOrderIds);
  const movedOrderIds = normalizePreviewDeliveryIds(candidate?.orderIds)
    .filter((orderId) => sourceOrderIds.includes(orderId));
  const sourceTruckId = normalizeTruckIdentity(candidate?.sourceTruckId);
  if (
    candidate?.destinationKind !== "suspended"
    || normalizePreviewDeliveryIds(candidate?.deliveryIds).length < 2
    || sourceOrderIds.length < 2
    || movedOrderIds.length === 0
    || !sourceTruckId
  ) {
    return null;
  }

  const resultPayload = resolveUndoReassignmentResultPayload(context);
  const assignment = (Array.isArray(resultPayload?.assignments) ? resultPayload.assignments : [])
    .find((item) => normalizeTruckIdentity(item?.truck_id || item?.truckId) === sourceTruckId);
  if (!assignment) {
    return null;
  }

  const currentOrderIds = normalizePreviewDeliveryIds(
    (Array.isArray(assignment.orders) ? assignment.orders : [])
      .map((item) => resolveReassignmentOrderId(item)),
  );
  if (
    currentOrderIds.length !== sourceOrderIds.length
    || currentOrderIds.some((orderId) => !sourceOrderIds.includes(orderId))
  ) {
    return null;
  }
  if (currentOrderIds.every((orderId, index) => orderId === sourceOrderIds[index])) {
    return { kind: "none" };
  }

  const movedIndexes = movedOrderIds
    .map((orderId) => sourceOrderIds.indexOf(orderId))
    .sort((left, right) => left - right);
  const firstMovedIndex = movedIndexes[0];
  const hasContiguousSourceBlock = movedIndexes.every((index, offset) => index === firstMovedIndex + offset);
  if (!hasContiguousSourceBlock) {
    return null;
  }

  const movedOrderIdSet = new Set(movedOrderIds);
  const nextAnchorOrderId = sourceOrderIds
    .slice(firstMovedIndex + movedIndexes.length)
    .find((orderId) => !movedOrderIdSet.has(orderId));
  const previousAnchorOrderId = sourceOrderIds
    .slice(0, firstMovedIndex)
    .reverse()
    .find((orderId) => !movedOrderIdSet.has(orderId));
  const targetOrderId = nextAnchorOrderId || previousAnchorOrderId || "";
  const placement = nextAnchorOrderId ? "before" : "after";
  if (!targetOrderId) {
    return null;
  }

  const loadingStepIdByOrderId = new Map();
  (Array.isArray(assignment.trips) ? assignment.trips : []).forEach((trip) => {
    const steps = Array.isArray(trip?.operational_steps)
      ? trip.operational_steps
      : (Array.isArray(trip?.operationalSteps) ? trip.operationalSteps : []);
    steps.forEach((step) => {
      const stepType = normalizeReassignmentText(step?.step_type || step?.stepType).toLowerCase();
      const orderId = resolveReassignmentOrderId(step);
      const stepId = normalizeReassignmentText(step?.step_id || step?.stepId);
      if (stepType === "loading" && orderId && stepId && !loadingStepIdByOrderId.has(orderId)) {
        loadingStepIdByOrderId.set(orderId, stepId);
      }
    });
  });
  const draggedStepIds = movedOrderIds.map((orderId) => loadingStepIdByOrderId.get(orderId)).filter(Boolean);
  const targetStepId = loadingStepIdByOrderId.get(targetOrderId) || "";
  if (draggedStepIds.length !== movedOrderIds.length || !targetStepId) {
    return null;
  }

  const expectedRuntimeRevision = normalizeReassignmentText(
    resultPayload?.runtime_revision
      || resultPayload?.runtimeRevision
      || context?.payload?.runtime_revision
      || context?.payload?.runtimeRevision,
  );
  if (!expectedRuntimeRevision) {
    return null;
  }

  return {
    expectedRuntimeRevision,
    kind: "reorder",
    payload: {
      dispatchPeriod: normalizeReassignmentDispatchPeriod(candidate.dispatchPeriod),
      draggedLabel: candidate.sourceLabel || "Groupe restauré",
      draggedOrderLabel: movedOrderIds[0],
      draggedSegmentId: draggedStepIds[0],
      draggedStepId: draggedStepIds[0],
      draggedStepIds: draggedStepIds.slice(1),
      placement,
      scope: "loads",
      targetLabel: targetOrderId,
      targetOrderLabel: targetOrderId,
      targetSegmentId: targetStepId,
      targetStepId,
      truckId: sourceTruckId,
      truckLabel: candidate.sourceTruckName || sourceTruckId,
    },
  };
}

export function finalizeUndoReassignmentCandidate(candidate = null, context = null) {
  if (!candidate) {
    return null;
  }

  const resultPayload = resolveUndoReassignmentResultPayload(context);
  const runtimeRevision = normalizeReassignmentText(
    resultPayload?.runtime_revision
      || resultPayload?.runtimeRevision
      || context?.payload?.runtime_revision
      || context?.payload?.runtimeRevision,
  );
  if (!runtimeRevision) {
    return null;
  }

  const deliveryIds = normalizePreviewDeliveryIds(candidate.deliveryIds);
  const destinationKind = candidate.destinationKind === "suspended" ? "suspended" : "truck";
  const groupStepIds = deliveryIds.length > 1 && destinationKind === "truck"
    ? resolveUndoReassignmentGroupStepIds(resultPayload, deliveryIds, candidate.targetTruckId)
    : [];
  if (
    deliveryIds.length > 1
    && destinationKind === "truck"
    && groupStepIds.length !== deliveryIds.length
  ) {
    return null;
  }

  return {
    ...candidate,
    deliveryId: deliveryIds[0] || candidate.deliveryId,
    deliveryIds,
    expectedRuntimeRevision: runtimeRevision,
    groupStepIds,
    isPending: false,
  };
}

export function resolveUndoReassignmentAction(candidate = null) {
  const deliveryIds = normalizePreviewDeliveryIds(candidate?.deliveryIds);
  if (!candidate || candidate.isPending || !candidate.deliveryId || deliveryIds.length === 0) {
    return null;
  }

  const isGroup = deliveryIds.length > 1;
  if (candidate.originKind === "suspended") {
    return isGroup ? "suspend-group" : "suspend-single";
  }
  if (candidate.originKind === "truck" && normalizeTruckIdentity(candidate.sourceTruckId)) {
    if (candidate.destinationKind === "suspended") {
      return isGroup ? "reactivate-group" : "reactivate-single";
    }
    return isGroup ? "move-group" : "move-single";
  }
  return null;
}

export function shouldInvalidateUndoReassignment(candidate = null, {
  dispatchDate = "",
  eveningRuntimeRevision = "",
  runtimeRevision = "",
} = {}) {
  if (!candidate || candidate.isPending) {
    return false;
  }

  const candidateDispatchDate = normalizeReassignmentText(candidate.dispatchDate);
  const currentDispatchDate = normalizeReassignmentText(dispatchDate);
  if (candidateDispatchDate && currentDispatchDate && candidateDispatchDate !== currentDispatchDate) {
    return true;
  }

  const expectedRuntimeRevision = normalizeReassignmentText(candidate.expectedRuntimeRevision);
  if (!expectedRuntimeRevision) {
    return true;
  }
  const currentRuntimeRevision = normalizeReassignmentDispatchPeriod(candidate.dispatchPeriod) === "evening"
    ? normalizeReassignmentText(eveningRuntimeRevision)
    : normalizeReassignmentText(runtimeRevision);
  return Boolean(currentRuntimeRevision && currentRuntimeRevision !== expectedRuntimeRevision);
}

function hasSameReassignmentIdentity(expectedIds = [], candidateIds = []) {
  return expectedIds.length === candidateIds.length
    && expectedIds.every((expectedId) => candidateIds.includes(expectedId));
}

export function resolveCanonicalReassignmentCandidateState({
  candidate = null,
  contexts = [],
  deliveryIds = [],
  orderIds = [],
} = {}) {
  const canonicalDeliveryIds = normalizePreviewDeliveryIds(deliveryIds);
  const canonicalOrderIds = normalizePreviewDeliveryIds(orderIds);
  const safeContexts = Array.isArray(contexts) ? contexts.filter(Boolean) : [];
  const localDeliveryIds = normalizePreviewDeliveryIds([
    candidate?.deliveryId,
    ...(Array.isArray(candidate?.deliveryIds) ? candidate.deliveryIds : []),
    ...safeContexts.flatMap((context) => [context?.deliveryId, context?.delivery_id]),
  ]);
  const localOrderIds = normalizePreviewDeliveryIds([
    candidate?.orderId,
    ...(Array.isArray(candidate?.orderIds) ? candidate.orderIds : []),
    ...safeContexts.flatMap((context) => [context?.orderId, context?.order_id]),
  ]);
  const hasCanonicalIdentity = (
    (!canonicalDeliveryIds.length || hasSameReassignmentIdentity(canonicalDeliveryIds, localDeliveryIds))
    && (!canonicalOrderIds.length || hasSameReassignmentIdentity(canonicalOrderIds, localOrderIds))
  );
  const resolvedCandidate = candidate && hasCanonicalIdentity ? candidate : null;
  const resolvedDeliveryIds = canonicalDeliveryIds.length > 0
    ? canonicalDeliveryIds
    : (resolvedCandidate ? localDeliveryIds : []);

  return {
    candidate: resolvedCandidate,
    deliveryId: resolvedDeliveryIds[0] || "",
    deliveryIds: resolvedDeliveryIds,
    requiresRemoteFetch: Boolean(candidate && !resolvedCandidate),
  };
}

export function resolveSuspendedPreviewDeliveryIds(item = null) {
  const deliveryIds = normalizePreviewDeliveryIds(item?.deliveryIds);
  const deliveryId = typeof item?.deliveryId === "string" ? item.deliveryId.trim() : "";
  if (deliveryId && !deliveryIds.includes(deliveryId)) {
    deliveryIds.unshift(deliveryId);
  }
  return deliveryIds;
}

export function buildSuspendedDeliveryPreviewCandidate(item = null, options = {}) {
  const buildCandidate = options?.isGroupDrag === true
    ? buildGroupedReassignmentDragCandidate
    : buildReassignmentDragCandidate;

  if (Array.isArray(item)) {
    const contexts = item.filter(Boolean);
    return (options?.isGroupDrag === true || contexts.length > 1)
      ? buildGroupedReassignmentDragCandidate(contexts, options)
      : buildReassignmentDragCandidate(contexts, options);
  }

  if (Array.isArray(item?.contexts)) {
    return buildCandidate(item.contexts, options);
  }

  const groupMemberItems = Array.isArray(item?.memberItems) ? item.memberItems.filter(Boolean) : [];
  const groupDeliveryIds = normalizePreviewDeliveryIds(item?.deliveryIds);
  if (item?.type === "group" || groupDeliveryIds.length > 1 || groupMemberItems.length > 1) {
    const contexts = (groupMemberItems.length ? groupMemberItems : groupDeliveryIds.map((deliveryId) => ({ deliveryId })))
      .map((memberItem) => ({
        compatibilities: Array.isArray(memberItem?.compatibilities) ? memberItem.compatibilities : [],
        delivery_id: typeof memberItem?.deliveryId === "string" ? memberItem.deliveryId : "",
      }))
      .filter((context) => context.delivery_id);
    return buildGroupedReassignmentDragCandidate(contexts, options);
  }

  return buildReassignmentDragCandidate([{
    compatibilities: Array.isArray(item?.compatibilities) ? item.compatibilities : [],
    delivery_id: typeof item?.deliveryId === "string" ? item.deliveryId : "",
  }], options);
}

export function shouldReuseSuspendedDeliveryPreview(
  reassignmentDragState = null,
  deliveryId = "",
  deliveryIds = [],
  scope = {},
) {
  const resolvedDeliveryId = typeof deliveryId === "string" ? deliveryId : "";
  const resolvedDeliveryIds = normalizePreviewDeliveryIds(deliveryIds);
  const resolvedStatus = typeof reassignmentDragState?.status === "string" ? reassignmentDragState.status : "";

  if (!resolvedDeliveryId || resolvedStatus === "error") {
    return false;
  }

  if (
    Object.prototype.hasOwnProperty.call(scope, "sourceDispatchPeriod")
    && normalizeReassignmentDispatchPeriod(reassignmentDragState?.sourceDispatchPeriod)
      !== normalizeReassignmentDispatchPeriod(scope.sourceDispatchPeriod)
  ) {
    return false;
  }

  if (
    Object.prototype.hasOwnProperty.call(scope, "sourceRuntimeRevision")
    && normalizeReassignmentText(reassignmentDragState?.sourceRuntimeRevision)
      !== normalizeReassignmentText(scope.sourceRuntimeRevision)
  ) {
    return false;
  }

  if (resolvedDeliveryIds.length > 1) {
    const activeDeliveryIds = normalizePreviewDeliveryIds(reassignmentDragState?.deliveryIds);
    return Boolean(
      reassignmentDragState
      && reassignmentDragState.dragKind === "suspended"
      && reassignmentDragState.isGroupDrag === true
      && activeDeliveryIds.length === resolvedDeliveryIds.length
      && activeDeliveryIds.every((activeDeliveryId, index) => activeDeliveryId === resolvedDeliveryIds[index])
    );
  }

  return Boolean(
    reassignmentDragState
    && reassignmentDragState.dragKind === "suspended"
    && reassignmentDragState.deliveryId === resolvedDeliveryId
  );
}

export function resolveFleetReassignmentDragNoticeState(reassignmentDragState, disabledTimelineRowCount = 0) {
  if (!reassignmentDragState) {
    return null;
  }

  return {
    ...reassignmentDragState,
    hiddenTruckCount: disabledTimelineRowCount,
  };
}

export function shouldFetchRemoteReassignmentCandidate({
  groupDeliveryIds = [],
  isGroupDrag = false,
  localCandidate = null,
  fleetTruckCount = 0,
  rebuildMode = "complete",
} = {}) {
  if (!localCandidate) {
    return true;
  }

  if (localCandidate.requiresLiveRefresh === true) {
    return true;
  }

  const compatibilityCount = Array.isArray(localCandidate.compatibilities)
    ? localCandidate.compatibilities.length
    : 0;

  if (compatibilityCount === 0) {
    return true;
  }

  if (isGroupDrag) {
    if (!localCandidate.deliveryId || !isDispatchFastReassignmentMode(rebuildMode)) {
      return true;
    }

    const expectedDeliveryIds = normalizePreviewDeliveryIds(groupDeliveryIds);
    const localDeliveryIds = normalizePreviewDeliveryIds(localCandidate.deliveryIds);
    return expectedDeliveryIds.length < 2
      || localDeliveryIds.length !== expectedDeliveryIds.length
      || expectedDeliveryIds.some((deliveryId) => !localDeliveryIds.includes(deliveryId));
  }

  const normalizedFleetTruckCount = Number.isFinite(Number(fleetTruckCount))
    ? Math.max(Math.round(Number(fleetTruckCount)), 0)
    : 0;
  const expectedCandidatePoolSize = Math.max(normalizedFleetTruckCount - 1, 0);

  return expectedCandidatePoolSize > 0 && compatibilityCount < expectedCandidatePoolSize;
}

export function buildDraggedDeliverySuspensionPayload(currentState = null) {
  if (currentState?.dragKind !== "assignment" || !currentState?.deliveryId) {
    return null;
  }

  const deliveryIds = normalizePreviewDeliveryIds(currentState.deliveryIds);
  const dispatchPeriod = normalizeReassignmentDispatchPeriod(currentState.sourceDispatchPeriod);
  const expectedRuntimeRevision = dispatchPeriod === "evening"
    ? normalizeReassignmentText(currentState.sourceRuntimeRevision)
    : "";
  if (deliveryIds.length > 1) {
    return {
      kind: "group",
      payload: {
        deliveryIds,
        dispatchPeriod,
        expectedRuntimeRevision,
        groupStepIds: Array.isArray(currentState.groupStepIds)
          ? currentState.groupStepIds
            .filter((stepId) => typeof stepId === "string" && stepId.trim())
            .map((stepId) => stepId.trim())
          : [],
        sourceGroupKey: typeof currentState.sourceGroupKey === "string" ? currentState.sourceGroupKey.trim() : "",
      },
    };
  }

  return {
    kind: "single",
    deliveryId: currentState.deliveryId,
    options: {
      dispatchPeriod,
      expectedRuntimeRevision,
      skipConfirmation: true,
    },
  };
}

function resolveBooleanLike(value) {
  if (value === true || value === 1) {
    return true;
  }
  if (typeof value !== "string") {
    return false;
  }
  return ["1", "true", "yes", "oui", "y", "o"].includes(value.trim().toLowerCase());
}

function buildEveningDompeurOverrideCompatibility(target = null, { sourceIsDompeur = false } = {}) {
  if (!sourceIsDompeur || resolveBooleanLike(target?.isDompeur ?? target?.is_dompeur)) {
    return null;
  }

  const truckId = normalizeTruckIdentity(target?.truckId || target?.truck_id);
  if (!truckId) {
    return null;
  }

  return {
    confirmation_code: "dompeur_override",
    detail: "Attention: cette commande Dompeur est normalement réservée aux camions dompeur. Cette réaffectation contourne cette règle.",
    is_assignable: true,
    is_current_truck: false,
    is_product_compatible: true,
    reason_code: "dompeur_override_required",
    reason_label: "Type de camion incompatible",
    requires_confirmation: true,
    time_window_diagnostics: [],
    time_window_mode: null,
    tone: "warning",
    truck_id: truckId,
    truck_name: normalizeReassignmentText(target?.truckName || target?.truck_name) || truckId,
  };
}

export function buildEveningReassignmentTargetState(eveningTimelineRows = [], {
  excludeDompeurTargets = false,
  requiresAspirator = false,
  requiresOntario = false,
  sourceIsDompeur = false,
  sourceProductLabel = "",
} = {}) {
  const rawTargets = (Array.isArray(eveningTimelineRows) ? eveningTimelineRows : [])
    .filter(Boolean)
    .map((row) => {
      const assignment = row?.assignment && typeof row.assignment === "object" ? row.assignment : {};
      const truckId = normalizeTruckIdentity(row?.assignmentId || assignment?.truck_id);
      if (!truckId) {
        return null;
      }

      return {
        aspirator: resolveBooleanLike(assignment.aspirator) ? "Oui" : assignment.aspirator,
        isDispatchDisabled: resolveBooleanLike(row?.isDispatchDisabled ?? assignment.is_dispatch_disabled),
        isDompeur: assignment.is_dompeur,
        isTruckProtected: resolveBooleanLike(row?.isTruckProtected ?? assignment.is_truck_protected),
        ontario: resolveBooleanLike(assignment.ontario) ? "Oui" : assignment.ontario,
        preferredGrainType: assignment.preferred_grain_type,
        truckId,
        truckName: normalizeReassignmentText(row?.truckLabel || assignment.truck_name) || truckId,
        truckType: assignment.truck_type,
        truck_raw_data: assignment.truck_raw_data,
      };
    })
    .filter(Boolean);
  const compatibleTargets = filterEndOfDayLoadTargetTrucks(rawTargets, {
    excludeDompeurTargets,
    requiresAspirator,
    requiresOntario,
    sourceProductLabel,
  });
  const actionableCompatibilityByTruckId = {};
  const compatibilityByTruckId = {};

  compatibleTargets.forEach((target) => {
    const truckId = normalizeTruckIdentity(target?.truckId || target?.truck_id);
    if (!truckId) {
      return;
    }
    const compatibility = {
      targetCompatibility: buildEveningDompeurOverrideCompatibility(target, { sourceIsDompeur }),
      truck_id: truckId,
      truck_name: normalizeReassignmentText(target?.truckName || target?.truck_name) || truckId,
    };
    actionableCompatibilityByTruckId[truckId] = compatibility;
    compatibilityByTruckId[truckId] = compatibility;
  });

  return {
    actionableCompatibilityByTruckId,
    compatibilityByTruckId,
    compatibleTruckIds: Object.keys(compatibilityByTruckId),
  };
}

export function excludeCurrentReassignmentTarget(targetState = null, sourceAssignmentId = "") {
  const normalizedSourceAssignmentId = normalizeTruckIdentity(sourceAssignmentId);
  const shouldKeepTarget = (targetId) => (
    !normalizedSourceAssignmentId
    || normalizeTruckIdentity(targetId) !== normalizedSourceAssignmentId
  );
  const filterTargetMap = (targetMap) => Object.fromEntries(
    Object.entries(targetMap && typeof targetMap === "object" ? targetMap : {})
      .filter(([targetId]) => shouldKeepTarget(targetId)),
  );

  return {
    actionableCompatibilityByTruckId: filterTargetMap(targetState?.actionableCompatibilityByTruckId),
    compatibilityByTruckId: filterTargetMap(targetState?.compatibilityByTruckId),
    compatibleTruckIds: (Array.isArray(targetState?.compatibleTruckIds) ? targetState.compatibleTruckIds : [])
      .filter(shouldKeepTarget),
  };
}

export function buildEveningStandardTransferDragPayload(
  currentState = null,
  targetAssignmentId = "",
  dispatchDate = "",
  targetPeriod = "evening",
) {
  const targetTruckId = normalizeTruckIdentity(targetAssignmentId);
  const normalizedTargetPeriod = normalizeReassignmentText(targetPeriod).toLowerCase() === "day" ? "day" : "evening";
  const compatibility = normalizedTargetPeriod === "day"
    ? currentState?.actionableCompatibilityByTruckId?.[targetTruckId] || null
    : currentState?.eveningActionableCompatibilityByTruckId?.[targetTruckId] || null;
  const resolvedDeliveryIds = normalizePreviewDeliveryIds(currentState?.deliveryIds);
  const deliveryIds = resolvedDeliveryIds.length > 0
    ? resolvedDeliveryIds
    : normalizePreviewDeliveryIds([currentState?.deliveryId]);
  const orderIds = normalizePreviewDeliveryIds(currentState?.orderIds);
  const normalizedDispatchDate = normalizeReassignmentText(dispatchDate);

  if (
    currentState?.status !== "ready"
    || !["assignment", "suspended"].includes(currentState?.dragKind)
    || !compatibility
    || !targetTruckId
    || !normalizedDispatchDate
    || deliveryIds.length === 0
  ) {
    return null;
  }

  const isGroupDrag = deliveryIds.length > 1;
  return {
    deliveryId: deliveryIds[0],
    deliveryIds,
    ...(orderIds.length > 0 ? { orderIds } : {}),
    dispatchDate: normalizedDispatchDate,
    groupStepIds: isGroupDrag && Array.isArray(currentState.groupStepIds)
      ? currentState.groupStepIds.filter((stepId) => normalizeReassignmentText(stepId))
      : [],
    rebuildMode: normalizeDispatchReassignmentMode(currentState.rebuildMode),
    scope: isGroupDrag ? "group" : "delivery",
    sourceDispatchPeriod: normalizeReassignmentDispatchPeriod(currentState.sourceDispatchPeriod),
    sourceGroupKey: isGroupDrag ? normalizeReassignmentText(currentState.sourceGroupKey) : "",
    sourceTruckId: normalizeTruckIdentity(currentState.sourceAssignmentId),
    targetCompatibility: compatibility.targetCompatibility || null,
    targetPeriod: normalizedTargetPeriod,
    targetTruckId: compatibility.truck_id || targetTruckId,
    targetTruckName: compatibility.truck_name || compatibility.truck_id || targetTruckId,
  };
}

export function shouldResetReassignmentTimelineScroll(reassignmentDragState, eveningTimelineRows = []) {
  return Boolean(reassignmentDragState)
    && (!Array.isArray(eveningTimelineRows) || eveningTimelineRows.length === 0);
}

export default function useFleetReassignmentDrag({
  assignmentInteractionsRef = null,
  closeSegmentActionPopover = null,
  dispatchDate = "",
  eveningRuntimeRevision = "",
  eveningTimelineRows = [],
  onTargetAssignmentSelect = null,
  runtimeRevision = "",
  timelineDispatchPeriod = "day",
  timelineRows = [],
  timelineRowsContainerRef = null,
  toggleTruckFilterDropdown = null,
} = {}) {
  const reassignmentDragAbortControllerRef = React.useRef(null);
  const reassignmentDragRequestIdRef = React.useRef(0);
  const reassignmentDragStateRef = React.useRef(null);
  const reassignmentPointerGestureRef = React.useRef(null);
  const suppressReassignmentClickRef = React.useRef(false);
  const [reassignmentDragState, setReassignmentDragState] = React.useState(null);
  const [undoReassignmentState, setUndoReassignmentState] = React.useState(null);
  const primaryTimelineDispatchPeriod = normalizeReassignmentDispatchPeriod(timelineDispatchPeriod);

  React.useEffect(() => {
    setUndoReassignmentState((currentState) => (
      shouldInvalidateUndoReassignment(currentState, {
        dispatchDate,
        eveningRuntimeRevision,
        runtimeRevision,
      })
        ? null
        : currentState
    ));
  }, [dispatchDate, eveningRuntimeRevision, runtimeRevision]);

  React.useEffect(() => {
    reassignmentDragStateRef.current = reassignmentDragState;
  }, [reassignmentDragState]);

  React.useEffect(() => {
    if (!shouldResetReassignmentTimelineScroll(reassignmentDragState, eveningTimelineRows)) {
      return;
    }

    const containerElement = timelineRowsContainerRef?.current;
    if (containerElement instanceof HTMLElement) {
      containerElement.scrollTop = 0;
    }
  }, [eveningTimelineRows, reassignmentDragState?.requestId, reassignmentDragState?.status, timelineRowsContainerRef]);

  const clearReassignmentPointerGesture = React.useCallback(() => {
    const gesture = reassignmentPointerGestureRef.current;
    if (!gesture) {
      return;
    }

    if (gesture.holdTimerId) {
      window.clearTimeout(gesture.holdTimerId);
    }

    gesture.cleanup?.();
    reassignmentPointerGestureRef.current = null;
  }, []);

  const resetReassignmentDragState = React.useCallback(() => {
    clearReassignmentPointerGesture();
    reassignmentDragAbortControllerRef.current?.abort();
    reassignmentDragAbortControllerRef.current = null;
    reassignmentDragRequestIdRef.current += 1;
    // Les événements pointerup/drop/dragend peuvent se suivre avant le prochain
    // effet React. Invalider aussi la référence synchrone empêche un même dépôt
    // d'être rejoué une seconde fois avec l'ancien rail source.
    reassignmentDragStateRef.current = null;
    setReassignmentDragState(null);
  }, [clearReassignmentPointerGesture]);

  React.useEffect(() => subscribePlanningStateChange((detail) => {
    if (
      !isPlanningStateChangeInScope(
        detail,
        readPlanningStateChangeScopeFromLocation(),
      )
    ) {
      return;
    }
    if (
      detail?.reasonCode === "runtime_revision_conflict"
      || detail?.reasonCode === "planning_mutation_in_progress"
    ) {
      resetReassignmentDragState();
    }
    if (detail?.reasonCode === "runtime_revision_conflict") {
      setUndoReassignmentState(null);
    }
  }), [resetReassignmentDragState]);

  React.useEffect(() => {
    return () => {
      clearReassignmentPointerGesture();
      reassignmentDragAbortControllerRef.current?.abort();
    };
  }, [clearReassignmentPointerGesture]);

  const buildInitialUndoMutationCallbacks = React.useCallback((undoCandidate, {
    onSuccess = null,
  } = {}) => ({
    onMutationError() {
      setUndoReassignmentState((currentState) => (
        isMatchingUndoReassignment(currentState, undoCandidate) ? null : currentState
      ));
    },
    onMutationStart() {
      setUndoReassignmentState((currentState) => (
        isMatchingUndoReassignment(currentState, undoCandidate)
          ? { ...currentState, isPending: true }
          : currentState
      ));
    },
    onMutationSuccess(context = {}) {
      setUndoReassignmentState((currentState) => (
        isMatchingUndoReassignment(currentState, undoCandidate)
          ? finalizeUndoReassignmentCandidate(currentState, context)
          : currentState
      ));
      onSuccess?.(context);
    },
  }), []);

  const clearReassignmentClickSuppression = React.useCallback(() => {
    suppressReassignmentClickRef.current = false;
  }, []);

  const consumeReassignmentClickSuppression = React.useCallback(() => {
    if (!suppressReassignmentClickRef.current) {
      return false;
    }

    suppressReassignmentClickRef.current = false;
    return true;
  }, []);

  const setHoveredReassignmentTarget = React.useCallback((targetAssignmentId = "", pointerPosition = null) => {
    setReassignmentDragState((currentState) => {
      if (!currentState) {
        return currentState;
      }

      const nextPointerClientX = Number.isFinite(Number(pointerPosition?.clientX))
        ? Number(pointerPosition.clientX)
        : currentState.pointerClientX;
      const nextPointerClientY = Number.isFinite(Number(pointerPosition?.clientY))
        ? Number(pointerPosition.clientY)
        : currentState.pointerClientY;
      const nextDispatchPeriod = normalizeReassignmentDispatchPeriod(pointerPosition?.dispatchPeriod);
      const sourceDispatchPeriod = normalizeReassignmentDispatchPeriod(currentState.sourceDispatchPeriod);
      const isCrossDispatchPeriodTarget = sourceDispatchPeriod !== nextDispatchPeriod;
      const compatibility = nextDispatchPeriod === "evening"
        ? currentState.eveningActionableCompatibilityByTruckId?.[targetAssignmentId] || null
        : currentState.actionableCompatibilityByTruckId?.[targetAssignmentId] || null;
      const nextHoveredTruckId = currentState.status === "ready"
        && targetAssignmentId
        && compatibility
        && (
          isCrossDispatchPeriodTarget
          || !isEquivalentReassignmentTarget(currentState, targetAssignmentId, compatibility)
        )
        ? targetAssignmentId
        : "";
      const nextHoveredDropZone = pointerPosition?.dropZone === "suspended"
        && (canDropActiveDeliveryOnSuspendedPeriod(currentState, nextDispatchPeriod)
          || canTransferSuspendedDeliveryToPeriod(currentState, nextDispatchPeriod))
        ? "suspended"
        : nextHoveredTruckId && nextDispatchPeriod === "evening"
          ? "evening"
        : nextHoveredTruckId
          ? "truck"
          : "";
      const nextHoveredDispatchPeriod = nextHoveredDropZone ? nextDispatchPeriod : "";
      // Camion grisé survolé : sa raison s'affiche dans la bulle du glisser.
      const nextHoveredBlockedTarget = currentState.status === "ready" && targetAssignmentId && !compatibility
        ? (nextDispatchPeriod === "evening"
          ? currentState.eveningBlockedReasonByTruckId
          : currentState.blockedReasonByTruckId)?.[targetAssignmentId] || null
        : null;

      if (
        currentState.hoveredTruckId === nextHoveredTruckId
        && currentState.hoveredBlockedTarget === nextHoveredBlockedTarget
        && currentState.hoveredDropZone === nextHoveredDropZone
        && currentState.hoveredDispatchPeriod === nextHoveredDispatchPeriod
        && currentState.pointerClientX === nextPointerClientX
        && currentState.pointerClientY === nextPointerClientY
      ) {
        return currentState;
      }

      return {
        ...currentState,
        hoveredBlockedTarget: nextHoveredBlockedTarget,
        hoveredDispatchPeriod: nextHoveredDispatchPeriod,
        hoveredDropZone: nextHoveredDropZone,
        hoveredTruckId: nextHoveredTruckId,
        pointerClientX: nextPointerClientX,
        pointerClientY: nextPointerClientY,
      };
    });
  }, []);

  const updateImmediateSegmentReassignment = React.useCallback((pointerPosition = null) => {
    const clientX = Number.isFinite(Number(pointerPosition?.clientX)) ? Number(pointerPosition.clientX) : null;
    const clientY = Number.isFinite(Number(pointerPosition?.clientY)) ? Number(pointerPosition.clientY) : null;
    if (clientX == null || clientY == null) {
      return false;
    }

    const dropTarget = resolveReassignmentDropTargetFromClientPoint(clientX, clientY);

    setHoveredReassignmentTarget(
      dropTarget.assignmentId,
      {
        clientX,
        clientY,
        dispatchPeriod: dropTarget.dispatchPeriod,
        dropZone: dropTarget.dropZone,
      },
    );
    return true;
  }, [setHoveredReassignmentTarget]);

  const startSegmentReassignmentPreview = React.useCallback((row, segment, pointerPosition = null, previewOverrides = null) => {
    if (isPlanningMutationActivityActive()) {
      return false;
    }

    const dragConfig = segment?.reassignmentDrag || null;
    if (!dragConfig?.stepId || !dragConfig?.stepType) {
      return false;
    }

    const groupDrag = resolveSegmentReassignmentGroupDrag(segment, previewOverrides?.groupDrag);
    const groupDeliveryIds = Array.isArray(groupDrag?.deliveryIds)
      ? groupDrag.deliveryIds.filter((deliveryId) => typeof deliveryId === "string" && deliveryId.trim())
      : [];
    const groupStepIds = Array.isArray(groupDrag?.groupStepIds)
      ? groupDrag.groupStepIds.filter((stepId) => typeof stepId === "string" && stepId.trim())
      : [];
    const isGroupDrag = groupDeliveryIds.length > 1;
    const immediateDeliveryId = isGroupDrag ? (groupDeliveryIds[0] || "") : resolveImmediateDeliveryId(segment);
    const orderIds = normalizePreviewDeliveryIds(
      isGroupDrag
        ? [
            ...(Array.isArray(groupDrag?.orderIds) ? groupDrag.orderIds : []),
            ...(Array.isArray(groupDrag?.reassignmentSegments)
              ? groupDrag.reassignmentSegments.map((memberSegment) => (
                  memberSegment?.operationOrderId
                  || memberSegment?.orderLabel
                  || memberSegment?.reassignmentDrag?.orderId
                ))
              : []),
          ]
        : [
            segment?.operationOrderId,
            segment?.orderLabel,
            segment?.manualBufferOrderId,
            segment?.reassignmentDrag?.orderId,
          ],
    );
    const rebuildMode = normalizeDispatchReassignmentMode(
      assignmentInteractionsRef?.current?.reassignmentRebuildMode,
    );

    assignmentInteractionsRef?.current?.onCloseReassignment?.();
    toggleTruckFilterDropdown?.(false);
    closeSegmentActionPopover?.();

    const requestId = reassignmentDragRequestIdRef.current + 1;
    reassignmentDragRequestIdRef.current = requestId;
    reassignmentDragAbortControllerRef.current?.abort();

    const previewContexts = isGroupDrag
      ? (Array.isArray(groupDrag?.previewContexts) ? groupDrag.previewContexts.filter(Boolean) : [])
      : (Array.isArray(segment?.reassignmentPreviewContexts) ? segment.reassignmentPreviewContexts.filter(Boolean) : []);
    const sourceProductLabel = resolveReassignmentSourceProductLabel(segment, groupDrag);
    const sourceDispatchPeriod = row?.dispatchPeriod === "evening" ? "evening" : "day";
    const targetFilterOptions = {
      excludeDompeurTargets: isGroupDrag,
      requiresAspirator: Boolean(segment?.requiresAspirator || (isGroupDrag && groupDrag?.requiresAspirator)),
      requiresOntario: Boolean(segment?.requiresOntario || (isGroupDrag && groupDrag?.requiresOntario)),
      sourceIsDompeur: resolveBooleanLike(row?.assignment?.is_dompeur),
      sourceProductLabel,
    };
    const emptyTargetState = { actionableCompatibilityByTruckId: {}, compatibilityByTruckId: {}, compatibleTruckIds: [] };
    const dayTimelineRows = primaryTimelineDispatchPeriod === "day" ? timelineRows : [];
    const eveningTargetTimelineRows = primaryTimelineDispatchPeriod === "evening" ? timelineRows : eveningTimelineRows;
    const dayCrossPeriodTargetState = sourceDispatchPeriod === "evening"
      ? buildEveningReassignmentTargetState(dayTimelineRows, targetFilterOptions)
      : emptyTargetState;
    const eveningCrossPeriodTargetState = sourceDispatchPeriod === "day"
      ? buildEveningReassignmentTargetState(eveningTargetTimelineRows, targetFilterOptions)
      : emptyTargetState;
    const sourceTimelineRows = sourceDispatchPeriod === primaryTimelineDispatchPeriod
      ? timelineRows
      : sourceDispatchPeriod === "evening" ? eveningTimelineRows : [];
    const candidateOptions = {
      requiresAspirator: Boolean(segment?.requiresAspirator || (isGroupDrag && groupDrag?.requiresAspirator)),
      requiresOntario: Boolean(segment?.requiresOntario || (isGroupDrag && groupDrag?.requiresOntario)),
      sourceOrderDompeurRequirements: resolveReassignmentSourceOrderDompeurRequirements(segment, groupDrag),
      sourceProductLabel,
      sourceTruckName: row?.truckLabel || row?.assignment?.truck_name || row?.assignmentId || "",
      truckCapabilityById: buildReassignmentVisibleTruckCapabilityIndex(sourceTimelineRows),
    };
    const buildCandidate = isGroupDrag ? buildGroupedReassignmentDragCandidate : buildReassignmentDragCandidate;
    const rawLocalCandidate = previewContexts.length
      ? buildCandidate(previewContexts, candidateOptions)
      : null;
    const canonicalDeliveryIds = normalizePreviewDeliveryIds(
      isGroupDrag ? groupDeliveryIds : [immediateDeliveryId],
    );
    const localCandidateState = resolveCanonicalReassignmentCandidateState({
      candidate: rawLocalCandidate,
      contexts: previewContexts,
      deliveryIds: canonicalDeliveryIds,
      orderIds,
    });
    const localCandidate = localCandidateState.candidate;
    const buildCandidateTargetState = (candidate = null) => ({
      actionableCompatibilityByTruckId: candidate?.actionableCompatibilityByTruckId || {},
      blockedReasonByTruckId: candidate?.blockedReasonByTruckId || {},
      compatibilityByTruckId: candidate?.compatibilityByTruckId || {},
      compatibleTruckIds: Array.isArray(candidate?.compatibilities)
        ? candidate.compatibilities.map((compatibility) => compatibility.truck_id)
        : [],
    });
    const localSamePeriodTargetState = buildCandidateTargetState(localCandidate);
    const dayTargetState = sourceDispatchPeriod === "day"
      ? localSamePeriodTargetState
      : dayCrossPeriodTargetState;
    const eveningTargetState = sourceDispatchPeriod === "evening"
      ? localSamePeriodTargetState
      : eveningCrossPeriodTargetState;
    const hasDayTargets = dayTargetState.compatibleTruckIds.length > 0;
    const hasEveningTargets = eveningTargetState.compatibleTruckIds.length > 0;
    const hasCrossPeriodTargets = sourceDispatchPeriod === "evening" ? hasDayTargets : hasEveningTargets;
    const crossPeriodTargetLabel = sourceDispatchPeriod === "evening" ? "Jour" : "Soir";
    const shouldFetchRemoteCandidate = localCandidateState.requiresRemoteFetch
      || shouldFetchRemoteReassignmentCandidate({
        groupDeliveryIds,
        isGroupDrag,
        localCandidate,
        fleetTruckCount: Array.isArray(sourceTimelineRows) ? sourceTimelineRows.length : 0,
        rebuildMode,
      });
    const groupPreviewLabel = isGroupDrag && typeof groupDrag?.label === "string" && groupDrag.label.trim()
      ? groupDrag.label.trim()
      : "";
    const groupPreviewMeta = isGroupDrag
      ? `${groupDrag?.memberCount || groupDeliveryIds.length} étape${(groupDrag?.memberCount || groupDeliveryIds.length) > 1 ? "s" : ""}`
      : "";
    const sourceOrderIds = isGroupDrag ? resolveUndoReassignmentSourceOrderIds(row) : [];
    const samePeriodTargetState = sourceDispatchPeriod === "evening" ? eveningTargetState : dayTargetState;
    const buildSamePeriodCandidatePatch = (candidate = null) => {
      const candidateTargetState = buildCandidateTargetState(candidate);
      return sourceDispatchPeriod === "evening"
        ? {
            eveningActionableCompatibilityByTruckId: candidateTargetState.actionableCompatibilityByTruckId,
            eveningBlockedReasonByTruckId: candidateTargetState.blockedReasonByTruckId,
            eveningCompatibilityByTruckId: candidateTargetState.compatibilityByTruckId,
            eveningCompatibleTruckIds: candidateTargetState.compatibleTruckIds,
          }
        : {
            actionableCompatibilityByTruckId: candidateTargetState.actionableCompatibilityByTruckId,
            blockedReasonByTruckId: candidateTargetState.blockedReasonByTruckId,
            compatibilityByTruckId: candidateTargetState.compatibilityByTruckId,
            compatibleTruckIds: candidateTargetState.compatibleTruckIds,
          };
    };
    const resolveSamePeriodHiddenTruckCount = (compatibilityByTruckId = {}) => sourceTimelineRows.reduce(
      (count, timelineRow) => {
        if (timelineRow.assignmentId === row?.assignmentId) {
          return count;
        }

        return compatibilityByTruckId[timelineRow.assignmentId] ? count : count + 1;
      },
      0,
    );
    const crossPeriodReadyMessage = `Déposez la commande sur un camion compatible du ${crossPeriodTargetLabel}.`;

    const baseDragState = {
      actionableCompatibilityByTruckId: dayTargetState.actionableCompatibilityByTruckId,
      blockedReasonByTruckId: dayTargetState.blockedReasonByTruckId || {},
      compatibilityByTruckId: dayTargetState.compatibilityByTruckId,
      compatibleTruckIds: dayTargetState.compatibleTruckIds,
      deliveryId: localCandidateState.deliveryId || immediateDeliveryId,
      ...(isGroupDrag ? { deliveryIds: localCandidateState.deliveryIds } : {}),
      ...(isGroupDrag && groupStepIds.length > 0 ? { groupStepIds } : {}),
      ...(orderIds.length > 0 ? { orderIds } : {}),
      dragKind: "assignment",
      eveningActionableCompatibilityByTruckId: eveningTargetState.actionableCompatibilityByTruckId,
      eveningBlockedReasonByTruckId: eveningTargetState.blockedReasonByTruckId || {},
      eveningCompatibilityByTruckId: eveningTargetState.compatibilityByTruckId,
      eveningCompatibleTruckIds: eveningTargetState.compatibleTruckIds,
      hiddenTruckCount: resolveSamePeriodHiddenTruckCount(samePeriodTargetState.compatibilityByTruckId),
      hoveredDispatchPeriod: "",
      hoveredDropZone: "",
      hoveredTruckId: "",
      isGroupDrag,
      message: "Préparation des camions compatibles…",
      pointerClientX: Number.isFinite(Number(pointerPosition?.clientX)) ? Number(pointerPosition.clientX) : 24,
      pointerClientY: Number.isFinite(Number(pointerPosition?.clientY)) ? Number(pointerPosition.clientY) : 24,
      requestId,
      rebuildMode,
      sourceAssignmentId: row?.assignmentId || "",
      sourceDispatchPeriod,
      sourceRuntimeRevision: sourceDispatchPeriod === "evening"
        ? normalizeReassignmentText(eveningRuntimeRevision)
        : "",
      sourceGroupKey: isGroupDrag ? (groupDrag?.sourceGroupKey || "") : "",
      sourceLabel: groupPreviewLabel || segment?.businessStep?.title || `${segment?.fromLabel || ""} → ${segment?.toLabel || ""}`.trim(),
      ...(sourceOrderIds.length > 0 ? { sourceOrderIds } : {}),
      sourceProductLabel,
      sourcePreviewMeta: groupPreviewMeta,
      sourceTruckName: row?.truckLabel || row?.assignment?.truck_name || row?.assignmentId || "Camion précédent",
      stepId: dragConfig.stepId,
      stepType: dragConfig.stepType,
    };

    const applyResolvedCandidate = (candidate) => {
      if (!candidate?.deliveryId) {
        setReassignmentDragState({
          ...baseDragState,
          message: hasCrossPeriodTargets
            ? crossPeriodReadyMessage
            : candidate?.errorMessage || candidate?.message || (isGroupDrag
              ? "Aucun camion compatible n'est disponible pour toutes les commandes du groupe."
              : "Aucun camion compatible n'est disponible pour ce segment dans le flux actuel."),
          status: hasCrossPeriodTargets ? "ready" : "error",
        });
        return;
      }

      const compatibilityByTruckId = candidate.compatibilityByTruckId || {};
      const resolvedDeliveryIds = canonicalDeliveryIds.length > 0
        ? canonicalDeliveryIds
        : normalizePreviewDeliveryIds([
            candidate.deliveryId,
            ...(Array.isArray(candidate.deliveryIds) ? candidate.deliveryIds : []),
          ]);
      setReassignmentDragState({
        ...baseDragState,
        ...buildSamePeriodCandidatePatch(candidate),
        deliveryId: resolvedDeliveryIds[0] || candidate.deliveryId,
        ...(resolvedDeliveryIds.length > 1 ? { deliveryIds: resolvedDeliveryIds } : {}),
        hiddenTruckCount: resolveSamePeriodHiddenTruckCount(compatibilityByTruckId),
        message: candidate.message || "",
        status: "ready",
      });
    };

    if (localCandidate && !shouldFetchRemoteCandidate) {
      applyResolvedCandidate(localCandidate);
      return true;
    }

    const controller = new AbortController();
    reassignmentDragAbortControllerRef.current = controller;
    if (localCandidate?.deliveryId) {
      applyResolvedCandidate(localCandidate);
    } else {
      setReassignmentDragState({
        ...baseDragState,
        message: hasCrossPeriodTargets ? crossPeriodReadyMessage : baseDragState.message,
        status: hasCrossPeriodTargets ? "ready" : "loading",
      });
    }

    const remoteSegments = isGroupDrag
      ? (Array.isArray(groupDrag?.reassignmentSegments) ? groupDrag.reassignmentSegments : [segment])
      : [segment];

    Promise.all(remoteSegments.map((remoteSegment) => {
      const remoteDragConfig = remoteSegment?.reassignmentDrag || dragConfig;
      return fetchReassignmentCandidates({
        dispatchDate,
        dispatchPeriod: sourceDispatchPeriod,
        rebuildMode,
        resolveLocalRebuild: false,
        signal: controller.signal,
        stepId: remoteDragConfig.stepId,
        stepType: remoteDragConfig.stepType,
      }).then((payload) => {
        const contexts = Array.isArray(payload?.contexts) ? payload.contexts.filter(Boolean) : [];
        return contexts[0] || null;
      });
    }))
      .then((remoteContexts) => {
        if (controller.signal.aborted || reassignmentDragRequestIdRef.current !== requestId) {
          return;
        }

        const remoteCandidateContexts = remoteContexts.filter(Boolean);
        const rawRemoteCandidate = buildCandidate(remoteCandidateContexts, candidateOptions);
        const remoteCandidateState = resolveCanonicalReassignmentCandidateState({
          candidate: rawRemoteCandidate,
          contexts: remoteCandidateContexts,
          deliveryIds: canonicalDeliveryIds,
          orderIds,
        });
        const candidate = remoteCandidateState.candidate;
        setReassignmentDragState((currentState) => {
          if (!currentState || currentState.requestId !== requestId) {
            return currentState;
          }

          if (!candidate?.deliveryId) {
            const resolvedDeliveryIds = remoteCandidateState.deliveryIds;
            return {
              ...currentState,
              ...buildSamePeriodCandidatePatch(null),
              deliveryId: remoteCandidateState.deliveryId || currentState.deliveryId,
              ...(isGroupDrag ? { deliveryIds: resolvedDeliveryIds } : {}),
              hiddenTruckCount: resolveSamePeriodHiddenTruckCount({}),
              message: hasCrossPeriodTargets
                ? crossPeriodReadyMessage
                : rawRemoteCandidate?.errorMessage || (remoteCandidateState.requiresRemoteFetch
                  ? "Les commandes retournées ne correspondent pas au segment déplacé."
                  : isGroupDrag
                    ? "Aucun camion compatible n'est disponible pour toutes les commandes du groupe."
                    : "Aucun camion compatible n'est disponible pour ce segment dans le flux actuel."),
              status: hasCrossPeriodTargets ? "ready" : "error",
            };
          }

          const resolvedDeliveryIds = canonicalDeliveryIds.length > 0
            ? canonicalDeliveryIds
            : normalizePreviewDeliveryIds([
                candidate.deliveryId,
                ...(Array.isArray(candidate.deliveryIds) ? candidate.deliveryIds : []),
              ]);

          return {
            ...currentState,
            ...buildSamePeriodCandidatePatch(candidate),
            deliveryId: resolvedDeliveryIds[0] || candidate.deliveryId,
            ...(resolvedDeliveryIds.length > 1 ? { deliveryIds: resolvedDeliveryIds } : {}),
            hiddenTruckCount: resolveSamePeriodHiddenTruckCount(candidate.compatibilityByTruckId),
            message: candidate.message || "",
            status: "ready",
          };
        });
      })
      .catch((fetchError) => {
        if (controller.signal.aborted || reassignmentDragRequestIdRef.current !== requestId) {
          return;
        }

        setReassignmentDragState((currentState) => {
          if (!currentState || currentState.requestId !== requestId) {
            return currentState;
          }
          if (localCandidate?.deliveryId && currentState.status === "ready") {
            return currentState;
          }

          return {
            ...currentState,
            message: hasCrossPeriodTargets
              ? crossPeriodReadyMessage
              : fetchError instanceof Error ? fetchError.message : "Impossible de charger les camions compatibles.",
            status: hasCrossPeriodTargets ? "ready" : "error",
          };
        });
      });

    return true;
  }, [assignmentInteractionsRef, closeSegmentActionPopover, dispatchDate, eveningRuntimeRevision, eveningTimelineRows, primaryTimelineDispatchPeriod, timelineRows, toggleTruckFilterDropdown]);

  const commitReassignmentToAssignment = React.useCallback((targetAssignmentId = "", dispatchPeriod = "day") => {
    if (isPlanningMutationActivityActive()) {
      resetReassignmentDragState();
      return false;
    }

    const currentState = reassignmentDragStateRef.current;
    if (currentState?.status !== "ready") {
      return false;
    }

    const normalizedDispatchPeriod = normalizeReassignmentDispatchPeriod(dispatchPeriod);
    if (currentState.dragKind === "suspended") {
      const sourceDispatchPeriod = normalizeReassignmentDispatchPeriod(currentState.sourceDispatchPeriod);
      if (!targetAssignmentId || !currentState.deliveryId || sourceDispatchPeriod !== normalizedDispatchPeriod) {
        return false;
      }

      const compatibility = normalizedDispatchPeriod === "evening"
        ? currentState.eveningActionableCompatibilityByTruckId?.[targetAssignmentId] || null
        : currentState.actionableCompatibilityByTruckId?.[targetAssignmentId] || null;
      if (!compatibility) {
        return false;
      }
      const expectedRuntimeRevision = normalizedDispatchPeriod === "evening"
        ? normalizeReassignmentText(currentState.sourceRuntimeRevision)
        : "";
      const deliveryIds = normalizePreviewDeliveryIds(currentState.deliveryIds);
      const orderIds = normalizePreviewDeliveryIds(currentState.orderIds);
      const targetTruckId = compatibility.truck_id || targetAssignmentId;
      const targetTruckName = compatibility.truck_name || compatibility.truck_id || targetAssignmentId;
      const undoCandidate = buildUndoReassignmentCandidate(currentState, {
        dispatchDate,
        dispatchPeriod: normalizedDispatchPeriod,
        targetTruckId,
        targetTruckName,
      });
      if (!undoCandidate) {
        return false;
      }
      const mutationCallbacks = buildInitialUndoMutationCallbacks(undoCandidate, {
        onSuccess() {
          onTargetAssignmentSelect?.({
            assignmentId: targetTruckId,
            dispatchPeriod: normalizedDispatchPeriod,
          });
        },
      });

      if (deliveryIds.length > 1) {
        const commitGroupReassignment = assignmentInteractionsRef?.current?.onCommitGroupReassignment;
        if (typeof commitGroupReassignment !== "function") {
          return false;
        }
        setUndoReassignmentState(undoCandidate);
        commitGroupReassignment({
          compatibility,
          deliveryId: deliveryIds[0],
          deliveryIds,
          dispatchPeriod: normalizedDispatchPeriod,
          expectedRuntimeRevision,
          groupStepIds: [],
          orderIds,
          rebuildMode: currentState.rebuildMode || "complete",
          ...mutationCallbacks,
          skipConfirmation: true,
          sourceGroupKey: currentState.sourceGroupKey || "",
          sourceTruckId: "",
          targetTruckId,
          targetTruckName,
        });
        resetReassignmentDragState();
        return true;
      }

      const reactivateDelivery = assignmentInteractionsRef?.current?.onReactivateDeliveryToTruck;
      if (typeof reactivateDelivery !== "function") {
        return false;
      }
      setUndoReassignmentState(undoCandidate);
      reactivateDelivery({
        deliveryId: currentState.deliveryId,
        dispatchPeriod: normalizedDispatchPeriod,
        expectedRuntimeRevision,
        ...mutationCallbacks,
        orderIds,
        rebuildMode: currentState.rebuildMode || "complete",
        skipConfirmation: true,
        targetCompatibility: compatibility,
        targetTruckId,
        targetTruckName,
      });
      resetReassignmentDragState();
      return true;
    }

    const compatibility = normalizedDispatchPeriod === "evening"
      ? currentState.eveningActionableCompatibilityByTruckId?.[targetAssignmentId]
      : currentState.actionableCompatibilityByTruckId?.[targetAssignmentId];
    const deliveryIds = normalizePreviewDeliveryIds([
      ...(Array.isArray(currentState.deliveryIds) ? currentState.deliveryIds : []),
      currentState.deliveryId,
    ]);
    const orderIds = normalizePreviewDeliveryIds(currentState.orderIds);
    const deliveryId = deliveryIds[0] || currentState.deliveryId;
    if (!compatibility || !deliveryId || isEquivalentReassignmentTarget(currentState, targetAssignmentId, compatibility)) {
      return false;
    }

    const sourceTruckId = currentState.sourceAssignmentId || "";
    const targetTruckId = compatibility.truck_id || targetAssignmentId;
    const targetTruckName = compatibility.truck_name || compatibility.truck_id || targetAssignmentId;
    const isGroupDrag = deliveryIds.length > 1;
    const commitReassignment = isGroupDrag
      ? assignmentInteractionsRef?.current?.onCommitGroupReassignment
      : assignmentInteractionsRef?.current?.onCommitReassignment;
    if (typeof commitReassignment !== "function") {
      return false;
    }
    const undoCandidate = buildUndoReassignmentCandidate(currentState, {
      dispatchDate,
      dispatchPeriod: normalizedDispatchPeriod,
      targetTruckId,
      targetTruckName,
    });
    if (!undoCandidate) {
      return false;
    }
    const mutationCallbacks = buildInitialUndoMutationCallbacks(undoCandidate);
    const commitPayload = {
      compatibility,
      deliveryId,
      dispatchPeriod: normalizedDispatchPeriod,
      expectedRuntimeRevision: normalizedDispatchPeriod === "evening"
        ? normalizeReassignmentText(currentState.sourceRuntimeRevision)
        : "",
      orderIds,
      rebuildMode: currentState.rebuildMode || "complete",
      skipConfirmation: true,
      ...mutationCallbacks,
      targetTruckId,
      targetTruckName,
    };

    setUndoReassignmentState(undoCandidate);
    if (isGroupDrag) {
      commitReassignment({
        ...commitPayload,
        deliveryIds,
        groupStepIds: Array.isArray(currentState.groupStepIds) ? currentState.groupStepIds : [],
        sourceGroupKey: currentState.sourceGroupKey || "",
        sourceTruckId,
      });
    } else {
      commitReassignment(commitPayload);
    }
    resetReassignmentDragState();
    return true;
  }, [assignmentInteractionsRef, buildInitialUndoMutationCallbacks, dispatchDate, onTargetAssignmentSelect, resetReassignmentDragState]);

  const commitReassignmentToEveningAssignment = React.useCallback((targetAssignmentId = "", targetPeriod = "evening") => {
    if (isPlanningMutationActivityActive()) {
      resetReassignmentDragState();
      return false;
    }

    const commitEveningTransfer = assignmentInteractionsRef?.current?.onAssignEveningStandard;
    if (typeof commitEveningTransfer !== "function") {
      return false;
    }

    const transferPayload = buildEveningStandardTransferDragPayload(
      reassignmentDragStateRef.current,
      targetAssignmentId,
      dispatchDate,
      targetPeriod,
    );
    if (!transferPayload) {
      return false;
    }

    commitEveningTransfer({
      ...transferPayload,
      onMutationSuccess() {
        onTargetAssignmentSelect?.({
          assignmentId: transferPayload.targetTruckId,
          dispatchPeriod: transferPayload.targetPeriod,
        });
      },
    });
    setUndoReassignmentState(null);
    resetReassignmentDragState();
    return true;
  }, [assignmentInteractionsRef, dispatchDate, onTargetAssignmentSelect, resetReassignmentDragState]);

  const commitReassignmentToDropTarget = React.useCallback((dropTarget = null) => {
    const sourceDispatchPeriod = reassignmentDragStateRef.current?.sourceDispatchPeriod === "evening" ? "evening" : "day";
    const targetDispatchPeriod = dropTarget?.dispatchPeriod === "evening" ? "evening" : "day";
    if (sourceDispatchPeriod !== targetDispatchPeriod) {
      return commitReassignmentToEveningAssignment(dropTarget?.assignmentId || "", targetDispatchPeriod);
    }
    return commitReassignmentToAssignment(dropTarget?.assignmentId || "", targetDispatchPeriod);
  }, [commitReassignmentToAssignment, commitReassignmentToEveningAssignment]);

  const commitDraggedDeliverySuspension = React.useCallback((targetDispatchPeriod = "day") => {
    if (isPlanningMutationActivityActive()) {
      resetReassignmentDragState();
      return false;
    }

    const currentState = reassignmentDragStateRef.current;
    if (canTransferSuspendedDeliveryToPeriod(currentState, targetDispatchPeriod)) {
      const transfer = assignmentInteractionsRef?.current?.onAssignEveningStandard;
      const payload = buildSuspendedPeriodTransferDragPayload(currentState, dispatchDate, targetDispatchPeriod);
      if (typeof transfer !== "function" || !payload) {
        return false;
      }
      transfer(payload);
      setUndoReassignmentState(null);
      resetReassignmentDragState();
      return true;
    }
    if (!canDropActiveDeliveryOnSuspendedPeriod(currentState, targetDispatchPeriod)) {
      return false;
    }

    const suspensionPayload = buildDraggedDeliverySuspensionPayload(currentState);
    const normalizedDispatchPeriod = normalizeReassignmentDispatchPeriod(targetDispatchPeriod);
    const undoCandidate = buildUndoReassignmentCandidate(currentState, {
      destinationKind: "suspended",
      dispatchDate,
      dispatchPeriod: normalizedDispatchPeriod,
    });
    if (!undoCandidate) {
      return false;
    }
    const mutationCallbacks = buildInitialUndoMutationCallbacks(undoCandidate);
    if (suspensionPayload?.kind === "group") {
      const suspendGroup = assignmentInteractionsRef?.current?.onSuspendDeliveryGroup;
      if (typeof suspendGroup !== "function") {
        return false;
      }
      setUndoReassignmentState(undoCandidate);
      suspendGroup({
        ...suspensionPayload.payload,
        ...mutationCallbacks,
      });
      resetReassignmentDragState();
      return true;
    }

    if (suspensionPayload?.kind !== "single") {
      return false;
    }

    const suspendDelivery = assignmentInteractionsRef?.current?.onSuspendDelivery;
    if (typeof suspendDelivery !== "function") {
      return false;
    }
    setUndoReassignmentState(undoCandidate);
    suspendDelivery(suspensionPayload.deliveryId, {
      ...suspensionPayload.options,
      ...mutationCallbacks,
    });
    resetReassignmentDragState();
    return true;
  }, [assignmentInteractionsRef, buildInitialUndoMutationCallbacks, dispatchDate, resetReassignmentDragState]);

  const finishImmediateSegmentReassignment = React.useCallback(({ shouldCommit = true, pointerPosition = null } = {}) => {
    const currentState = reassignmentDragStateRef.current;
    if (!currentState) {
      return false;
    }

    const clientX = Number.isFinite(Number(pointerPosition?.clientX))
      ? Number(pointerPosition.clientX)
      : currentState.pointerClientX;
    const clientY = Number.isFinite(Number(pointerPosition?.clientY))
      ? Number(pointerPosition.clientY)
      : currentState.pointerClientY;

    if (shouldCommit) {
      const dropTarget = resolveReassignmentDropTargetFromClientPoint(clientX, clientY);
      if (dropTarget.dropZone === "suspended") {
        if (commitDraggedDeliverySuspension(dropTarget.dispatchPeriod)) {
          return true;
        }
      }

      if (commitReassignmentToDropTarget(dropTarget)) {
        return true;
      }
    }

    resetReassignmentDragState();
    return false;
  }, [commitDraggedDeliverySuspension, commitReassignmentToDropTarget, resetReassignmentDragState]);

  const startImmediateSegmentReassignment = React.useCallback((row, segment, pointerPosition = null, previewOverrides = null) => {
    if (isPlanningMutationActivityActive()) {
      return false;
    }

    clearReassignmentPointerGesture();
    suppressReassignmentClickRef.current = true;
    const previewStarted = startSegmentReassignmentPreview(row, segment, pointerPosition, previewOverrides);
    if (!previewStarted) {
      return false;
    }

    updateImmediateSegmentReassignment(pointerPosition);
    return true;
  }, [clearReassignmentPointerGesture, startSegmentReassignmentPreview, updateImmediateSegmentReassignment]);

  const handleUndoReassignment = React.useCallback(() => {
    const action = resolveUndoReassignmentAction(undoReassignmentState);
    if (!action) {
      return false;
    }

    const undoSnapshot = undoReassignmentState;
    const mutationCallbacks = {
      onMutationError(context = {}) {
        setUndoReassignmentState((currentState) => {
          if (!isMatchingUndoReassignment(currentState, undoSnapshot)) {
            return currentState;
          }
          if (normalizeReassignmentText(context?.reasonCode) === "runtime_revision_conflict") {
            return null;
          }

          return {
            ...currentState,
            isPending: false,
          };
        });
      },
      onMutationStart() {
        setUndoReassignmentState((currentState) => {
          if (!isMatchingUndoReassignment(currentState, undoSnapshot)) {
            return currentState;
          }

          return {
            ...currentState,
            isPending: true,
          };
        });
      },
      onMutationSuccess() {
        setUndoReassignmentState((currentState) => {
          if (!isMatchingUndoReassignment(currentState, undoSnapshot)) {
            return currentState;
          }

          return null;
        });
      },
    };
    const dispatchPeriod = normalizeReassignmentDispatchPeriod(undoSnapshot.dispatchPeriod);
    const commonPayload = {
      deliveryId: undoSnapshot.deliveryId,
      dispatchPeriod,
      expectedRuntimeRevision: undoSnapshot.expectedRuntimeRevision || "",
      isUndo: true,
      ...mutationCallbacks,
    };

    if (action === "reactivate-group") {
      const commitGroupReassignment = assignmentInteractionsRef?.current?.onCommitGroupReassignment;
      if (typeof commitGroupReassignment !== "function") {
        return false;
      }
      const commitSegmentReorder = assignmentInteractionsRef?.current?.onCommitSegmentReorder;
      commitGroupReassignment({
        ...commonPayload,
        allowReturnToOptimized: true,
        deliveryIds: undoSnapshot.deliveryIds,
        groupStepIds: [],
        orderIds: undoSnapshot.orderIds,
        rebuildMode: undoSnapshot.rebuildMode,
        onMutationSuccess(context = {}) {
          const orderRestore = buildUndoReassignmentGroupOrderRestore(undoSnapshot, context);
          if (orderRestore?.kind !== "reorder" || typeof commitSegmentReorder !== "function") {
            mutationCallbacks.onMutationSuccess(context);
            return;
          }

          window.setTimeout(() => {
            commitSegmentReorder({
              ...orderRestore.payload,
              expectedRuntimeRevision: orderRestore.expectedRuntimeRevision,
              isUndo: true,
              onMutationError() {
                setUndoReassignmentState((currentState) => (
                  isMatchingUndoReassignment(currentState, undoSnapshot) ? null : currentState
                ));
              },
              onMutationStart() {
                setUndoReassignmentState((currentState) => (
                  isMatchingUndoReassignment(currentState, undoSnapshot)
                    ? { ...currentState, isPending: true }
                    : currentState
                ));
              },
              onMutationSuccess() {
                setUndoReassignmentState((currentState) => (
                  isMatchingUndoReassignment(currentState, undoSnapshot) ? null : currentState
                ));
              },
            });
          }, 0);
        },
        skipConfirmation: true,
        sourceGroupKey: "",
        sourceTruckId: "",
        targetTruckId: undoSnapshot.sourceTruckId,
        targetTruckName: undoSnapshot.sourceTruckName,
      });
      return true;
    }

    if (action === "reactivate-single") {
      const reactivateDelivery = assignmentInteractionsRef?.current?.onReactivateDeliveryToTruck;
      if (typeof reactivateDelivery !== "function") {
        return false;
      }
      reactivateDelivery({
        ...commonPayload,
        orderIds: undoSnapshot.orderIds,
        rebuildMode: undoSnapshot.rebuildMode,
        skipConfirmation: true,
        targetTruckId: undoSnapshot.sourceTruckId,
        targetTruckName: undoSnapshot.sourceTruckName,
      });
      return true;
    }

    if (action === "move-group") {
      const commitGroupReassignment = assignmentInteractionsRef?.current?.onCommitGroupReassignment;
      if (typeof commitGroupReassignment !== "function") {
        return false;
      }
      commitGroupReassignment({
        ...commonPayload,
        allowReturnToOptimized: true,
        deliveryIds: undoSnapshot.deliveryIds,
        groupStepIds: undoSnapshot.groupStepIds,
        orderIds: undoSnapshot.orderIds,
        rebuildMode: undoSnapshot.rebuildMode,
        sourceGroupKey: "",
        sourceTruckId: undoSnapshot.targetTruckId,
        targetTruckId: undoSnapshot.sourceTruckId,
        targetTruckName: undoSnapshot.sourceTruckName,
      });
      return true;
    }

    if (action === "move-single") {
      const commitReassignment = assignmentInteractionsRef?.current?.onCommitReassignment;
      if (typeof commitReassignment !== "function") {
        return false;
      }
      commitReassignment({
        ...commonPayload,
        allowReturnToOptimized: true,
        orderIds: undoSnapshot.orderIds,
        rebuildMode: undoSnapshot.rebuildMode,
        targetTruckId: undoSnapshot.sourceTruckId,
        targetTruckName: undoSnapshot.sourceTruckName,
      });
      return true;
    }

    if (action === "suspend-group") {
      const suspendGroup = assignmentInteractionsRef?.current?.onSuspendDeliveryGroup;
      if (typeof suspendGroup !== "function") {
        return false;
      }
      suspendGroup({
        ...commonPayload,
        deliveryIds: undoSnapshot.deliveryIds,
        groupStepIds: undoSnapshot.groupStepIds,
        sourceGroupKey: "",
      });
      return true;
    }

    if (action !== "suspend-single") {
      return false;
    }

    const suspendDelivery = assignmentInteractionsRef?.current?.onSuspendDelivery;
    if (typeof suspendDelivery !== "function") {
      return false;
    }
    suspendDelivery(undoSnapshot.deliveryId, {
      dispatchPeriod,
      expectedRuntimeRevision: undoSnapshot.expectedRuntimeRevision || "",
      ...mutationCallbacks,
      skipConfirmation: true,
    });

    return true;
  }, [assignmentInteractionsRef, undoReassignmentState]);

  const finishSuspendedDeliveryDropAtPoint = React.useCallback((clientX, clientY, { shouldCommit = true } = {}) => {
    const currentState = reassignmentDragStateRef.current;
    if (!currentState || currentState.dragKind !== "suspended") {
      return false;
    }

    if (shouldCommit) {
      const dropTarget = resolveReassignmentDropTargetFromClientPoint(clientX, clientY);
      if (dropTarget.dropZone === "suspended" && commitDraggedDeliverySuspension(dropTarget.dispatchPeriod)) {
        return true;
      }
      if (
        dropTarget.dropZone === "truck"
        && commitReassignmentToDropTarget(dropTarget)
      ) {
        return true;
      }
    }

    resetReassignmentDragState();
    return false;
  }, [commitDraggedDeliverySuspension, commitReassignmentToDropTarget, resetReassignmentDragState]);

  const handleReassignmentDragEnd = React.useCallback((event = null) => {
    const currentState = reassignmentDragStateRef.current;
    if (!currentState) {
      return;
    }

    if (currentState.dragKind === "suspended") {
      const clientX = Number.isFinite(Number(event?.clientX))
        ? Number(event.clientX)
        : currentState.pointerClientX;
      const clientY = Number.isFinite(Number(event?.clientY))
        ? Number(event.clientY)
        : currentState.pointerClientY;
      finishSuspendedDeliveryDropAtPoint(clientX, clientY);
      return;
    }

    resetReassignmentDragState();
  }, [finishSuspendedDeliveryDropAtPoint, resetReassignmentDragState]);

  const handleSegmentReassignmentPointerDown = React.useCallback((event, row, segment) => {
    const dragConfig = segment?.reassignmentDrag || null;
    if (event.button !== 0 || !event.isPrimary || !dragConfig?.stepId || !dragConfig?.stepType) {
      return;
    }

    clearReassignmentPointerGesture();
    suppressReassignmentClickRef.current = false;

    const gesture = {
      cleanup: null,
      holdTimerId: null,
      lastClientX: event.clientX,
      lastClientY: event.clientY,
      pointerId: event.pointerId,
      previewStarted: false,
      row,
      segment,
      startX: event.clientX,
      startY: event.clientY,
    };

    const startPreview = () => {
      if (gesture.previewStarted) {
        return;
      }

      gesture.previewStarted = startSegmentReassignmentPreview(
        gesture.row,
        gesture.segment,
        { clientX: gesture.lastClientX, clientY: gesture.lastClientY },
      );
      if (!gesture.previewStarted) {
        return;
      }

      suppressReassignmentClickRef.current = true;
      const dropTarget = resolveReassignmentDropTargetFromClientPoint(gesture.lastClientX, gesture.lastClientY);
      setHoveredReassignmentTarget(
        dropTarget.assignmentId,
        {
          clientX: gesture.lastClientX,
          clientY: gesture.lastClientY,
          dispatchPeriod: dropTarget.dispatchPeriod,
          dropZone: dropTarget.dropZone,
        },
      );
    };

    const finishGesture = (shouldCommit, pointerEvent = null) => {
      if (pointerEvent && pointerEvent.pointerId !== gesture.pointerId) {
        return;
      }

      const clientX = Number.isFinite(Number(pointerEvent?.clientX)) ? pointerEvent.clientX : gesture.lastClientX;
      const clientY = Number.isFinite(Number(pointerEvent?.clientY)) ? pointerEvent.clientY : gesture.lastClientY;
      const hasPointerMoved = hasReassignmentPointerMoved(
        { clientX: gesture.startX, clientY: gesture.startY },
        { clientX, clientY },
      );
      clearReassignmentPointerGesture();

      if (!gesture.previewStarted) {
        return;
      }

      suppressReassignmentClickRef.current = true;
      const dropTarget = resolveReassignmentDropTargetFromClientPoint(clientX, clientY);
      if (
        shouldCommit
        && hasPointerMoved
        && dropTarget.dropZone === "suspended"
        && commitDraggedDeliverySuspension(dropTarget.dispatchPeriod)
      ) {
        return;
      }

      if (shouldCommit && hasPointerMoved && commitReassignmentToDropTarget(dropTarget)) {
        return;
      }

      resetReassignmentDragState();
    };

    const handleWindowPointerMove = (moveEvent) => {
      if (moveEvent.pointerId !== gesture.pointerId) {
        return;
      }

      gesture.lastClientX = moveEvent.clientX;
      gesture.lastClientY = moveEvent.clientY;

      if (!gesture.previewStarted) {
        const deltaX = moveEvent.clientX - gesture.startX;
        const deltaY = moveEvent.clientY - gesture.startY;
        if (Math.hypot(deltaX, deltaY) >= REASSIGNMENT_POINTER_MOVE_THRESHOLD_PX) {
          if (gesture.holdTimerId) {
            window.clearTimeout(gesture.holdTimerId);
            gesture.holdTimerId = null;
          }
          startPreview();
        }
      }

      if (gesture.previewStarted) {
        const dropTarget = resolveReassignmentDropTargetFromClientPoint(moveEvent.clientX, moveEvent.clientY);
        setHoveredReassignmentTarget(
          dropTarget.assignmentId,
          {
            clientX: moveEvent.clientX,
            clientY: moveEvent.clientY,
            dispatchPeriod: dropTarget.dispatchPeriod,
            dropZone: dropTarget.dropZone,
          },
        );
      }
    };

    const handleWindowPointerUp = (upEvent) => {
      finishGesture(true, upEvent);
    };

    const handleWindowPointerCancel = (cancelEvent) => {
      finishGesture(false, cancelEvent);
    };

    const handleWindowBlur = () => {
      finishGesture(false);
    };

    const handleWindowKeyDown = (keyEvent) => {
      if (keyEvent.key !== "Escape") {
        return;
      }

      keyEvent.preventDefault();
      finishGesture(false);
    };

    gesture.cleanup = () => {
      window.removeEventListener("pointermove", handleWindowPointerMove, true);
      window.removeEventListener("pointerup", handleWindowPointerUp, true);
      window.removeEventListener("pointercancel", handleWindowPointerCancel, true);
      window.removeEventListener("blur", handleWindowBlur);
      window.removeEventListener("keydown", handleWindowKeyDown, true);
    };

    gesture.holdTimerId = window.setTimeout(() => {
      gesture.holdTimerId = null;
      startPreview();
    }, REASSIGNMENT_POINTER_HOLD_DELAY_MS);

    window.addEventListener("pointermove", handleWindowPointerMove, true);
    window.addEventListener("pointerup", handleWindowPointerUp, true);
    window.addEventListener("pointercancel", handleWindowPointerCancel, true);
    window.addEventListener("blur", handleWindowBlur);
    window.addEventListener("keydown", handleWindowKeyDown, true);
    reassignmentPointerGestureRef.current = gesture;
  }, [clearReassignmentPointerGesture, commitReassignmentToDropTarget, resetReassignmentDragState, setHoveredReassignmentTarget, startSegmentReassignmentPreview]);

  const handleSegmentReassignmentDragStart = React.useCallback((event, row, segment) => {
    const dragConfig = segment?.reassignmentDrag || null;
    if (!dragConfig?.stepId || !dragConfig?.stepType) {
      event.preventDefault();
      return;
    }

    event.stopPropagation();
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = "move";
      try {
        event.dataTransfer.setData("text/plain", dragConfig.stepId);
      } catch {
        event.dataTransfer.setData("text", dragConfig.stepId);
      }
    }

    startSegmentReassignmentPreview(row, segment, { clientX: event.clientX, clientY: event.clientY });
  }, [startSegmentReassignmentPreview]);

  const startSuspendedDeliveryPreview = React.useCallback((item = null, pointerPosition = null) => {
    if (isPlanningMutationActivityActive()) {
      return false;
    }

    const deliveryIds = resolveSuspendedPreviewDeliveryIds(item);
    const deliveryId = deliveryIds[0] || "";
    const orderIds = normalizePreviewDeliveryIds(
      Array.isArray(item?.orderIds) && item.orderIds.length > 0
        ? item.orderIds
        : [item?.orderLabel],
    );
    const isGroupDrag = deliveryIds.length > 1;
    const rebuildMode = normalizeDispatchReassignmentMode(
      assignmentInteractionsRef?.current?.reassignmentRebuildMode,
    );
    const sourceDispatchPeriod = normalizeReassignmentDispatchPeriod(
      item?.dispatchPeriod || item?.sourceDispatchPeriod,
    );
    const sourceRuntimeRevision = sourceDispatchPeriod === "evening"
      ? normalizeReassignmentText(item?.runtimeRevision || item?.expectedRuntimeRevision || eveningRuntimeRevision)
      : normalizeReassignmentText(item?.runtimeRevision || item?.expectedRuntimeRevision || runtimeRevision);
    if (!deliveryId || item?.isPending) {
      return false;
    }

    const previewDeliveryIds = isGroupDrag ? deliveryIds : [deliveryId];
    const currentState = reassignmentDragStateRef.current;
    if (
      currentState?.rebuildMode === rebuildMode
      && shouldReuseSuspendedDeliveryPreview(currentState, deliveryId, previewDeliveryIds, {
        sourceDispatchPeriod,
        sourceRuntimeRevision,
      })
    ) {
      setReassignmentDragState((activeState) => {
        if (
          activeState?.rebuildMode !== rebuildMode
          || !shouldReuseSuspendedDeliveryPreview(activeState, deliveryId, previewDeliveryIds, {
            sourceDispatchPeriod,
            sourceRuntimeRevision,
          })
        ) {
          return activeState;
        }

        const nextPointerClientX = Number.isFinite(Number(pointerPosition?.clientX))
          ? Number(pointerPosition.clientX)
          : activeState.pointerClientX;
        const nextPointerClientY = Number.isFinite(Number(pointerPosition?.clientY))
          ? Number(pointerPosition.clientY)
          : activeState.pointerClientY;

        if (
          activeState.pointerClientX === nextPointerClientX
          && activeState.pointerClientY === nextPointerClientY
        ) {
          return activeState;
        }

        return {
          ...activeState,
          pointerClientX: nextPointerClientX,
          pointerClientY: nextPointerClientY,
        };
      });
      return true;
    }

    assignmentInteractionsRef?.current?.onCloseReassignment?.();
    toggleTruckFilterDropdown?.(false);
    closeSegmentActionPopover?.();

    const requestId = reassignmentDragRequestIdRef.current + 1;
    reassignmentDragRequestIdRef.current = requestId;
    reassignmentDragAbortControllerRef.current?.abort();

    const sourceProductLabel = resolveSuspendedDeliverySourceProductLabel(item);
    const dayTimelineRows = primaryTimelineDispatchPeriod === "day" ? timelineRows : [];
    const eveningTargetTimelineRows = primaryTimelineDispatchPeriod === "evening" ? timelineRows : eveningTimelineRows;
    const sourceTimelineRows = sourceDispatchPeriod === primaryTimelineDispatchPeriod
      ? timelineRows
      : sourceDispatchPeriod === "evening" ? eveningTimelineRows : [];
    const candidateOptions = {
      isGroupDrag,
      requiresAspirator: Boolean(item?.requiresAspirator),
      requiresOntario: Boolean(item?.requiresOntario),
      sourceProductLabel,
      truckCapabilityById: buildReassignmentVisibleTruckCapabilityIndex(sourceTimelineRows),
    };
    const targetFilterOptions = {
      excludeDompeurTargets: isGroupDrag,
      requiresAspirator: candidateOptions.requiresAspirator,
      requiresOntario: candidateOptions.requiresOntario,
      sourceIsDompeur: resolveBooleanLike(item?.isDompeur ?? item?.is_dompeur),
      sourceProductLabel,
    };
    const dayCrossPeriodTargetState = sourceDispatchPeriod === "evening"
      ? buildEveningReassignmentTargetState(dayTimelineRows, targetFilterOptions)
      : { actionableCompatibilityByTruckId: {}, compatibilityByTruckId: {}, compatibleTruckIds: [] };
    const eveningCrossPeriodTargetState = sourceDispatchPeriod === "day"
      ? buildEveningReassignmentTargetState(eveningTargetTimelineRows, targetFilterOptions)
      : { actionableCompatibilityByTruckId: {}, compatibilityByTruckId: {}, compatibleTruckIds: [] };
    const localCandidate = buildSuspendedDeliveryPreviewCandidate(item, candidateOptions);
    const hasLocalCandidate = Boolean(
      localCandidate?.deliveryId
      && Array.isArray(localCandidate?.compatibilities)
      && localCandidate.compatibilities.length > 0
    );
    const canUseLocalCandidate = hasLocalCandidate;
    const hasDayTargets = sourceDispatchPeriod === "day"
      ? canUseLocalCandidate
      : dayCrossPeriodTargetState.compatibleTruckIds.length > 0;
    const hasEveningTargets = sourceDispatchPeriod === "evening"
      ? canUseLocalCandidate
      : eveningCrossPeriodTargetState.compatibleTruckIds.length > 0;
    const hasCrossPeriodTargets = sourceDispatchPeriod === "evening" ? hasDayTargets : hasEveningTargets;
    const hasImmediateTarget = canUseLocalCandidate || hasCrossPeriodTargets;

    const controller = new AbortController();
    reassignmentDragAbortControllerRef.current = controller;
    const sourceAssignmentId = typeof item?.currentTruckId === "string" ? item.currentTruckId : "";
    const sourceLabel = item?.title || item?.deliveryName || item?.orderLabel || (isGroupDrag ? "Groupe suspendu" : "Commande suspendue");
    const localReadyMessage = localCandidate?.message || (isGroupDrag
      ? `${sourceLabel}. Déposez ce groupe sur un camion compatible pour le réactiver.`
      : (item?.deliveryName || item?.title
          ? `${item.deliveryName || item.title}. Déposez cette commande sur un camion compatible pour la réactiver.`
          : "Déposez cette commande sur un camion compatible pour la réactiver."));
    const localActionableCompatibilityByTruckId = canUseLocalCandidate
      ? localCandidate.actionableCompatibilityByTruckId || {}
      : {};
    const localCompatibilityByTruckId = canUseLocalCandidate
      ? localCandidate.compatibilityByTruckId || {}
      : {};
    const localCompatibleTruckIds = canUseLocalCandidate
      ? localCandidate.compatibilities.map((compatibility) => compatibility.truck_id)
      : [];
    const dayTargetState = sourceDispatchPeriod === "day"
      ? {
          actionableCompatibilityByTruckId: localActionableCompatibilityByTruckId,
          compatibilityByTruckId: localCompatibilityByTruckId,
          compatibleTruckIds: localCompatibleTruckIds,
        }
      : dayCrossPeriodTargetState;
    const eveningTargetState = sourceDispatchPeriod === "evening"
      ? {
          actionableCompatibilityByTruckId: localActionableCompatibilityByTruckId,
          compatibilityByTruckId: localCompatibilityByTruckId,
          compatibleTruckIds: localCompatibleTruckIds,
        }
      : eveningCrossPeriodTargetState;
    setReassignmentDragState({
      actionableCompatibilityByTruckId: dayTargetState.actionableCompatibilityByTruckId,
      compatibilityByTruckId: dayTargetState.compatibilityByTruckId,
      compatibleTruckIds: dayTargetState.compatibleTruckIds,
      deliveryId,
      ...(isGroupDrag ? { deliveryIds: previewDeliveryIds } : {}),
      ...(orderIds.length > 0 ? { orderIds } : {}),
      dragKind: "suspended",
      eveningActionableCompatibilityByTruckId: eveningTargetState.actionableCompatibilityByTruckId,
      eveningCompatibilityByTruckId: eveningTargetState.compatibilityByTruckId,
      eveningCompatibleTruckIds: eveningTargetState.compatibleTruckIds,
      hiddenTruckCount: timelineRows.reduce(
        (count, timelineRow) => (dayTargetState.compatibilityByTruckId?.[timelineRow.assignmentId] ? count : count + 1),
        0,
      ),
      hoveredDispatchPeriod: "",
      hoveredDropZone: "",
      hoveredTruckId: "",
      isGroupDrag,
      message: hasImmediateTarget
        ? localReadyMessage
        : "Préparation des camions compatibles…",
      pointerClientX: Number.isFinite(Number(pointerPosition?.clientX)) ? Number(pointerPosition.clientX) : 24,
      pointerClientY: Number.isFinite(Number(pointerPosition?.clientY)) ? Number(pointerPosition.clientY) : 24,
      requestId,
      rebuildMode,
      sourceAssignmentId,
      sourceDispatchPeriod,
      sourceRuntimeRevision,
      sourceGroupKey: isGroupDrag ? (item?.sourceGroupKey || "") : "",
      sourceLabel,
      sourceProductLabel,
      sourcePreviewMeta: isGroupDrag ? `${previewDeliveryIds.length} commandes` : "",
      sourceTruckName: "Suspendues",
      status: hasImmediateTarget ? "ready" : "loading",
      stepId: "",
      stepType: "delivery",
    });

    Promise.all(previewDeliveryIds.map((currentDeliveryId) => fetchSuspendedDeliveryReactivationCandidates({
      deliveryId: currentDeliveryId,
      dispatchDate,
      dispatchPeriod: sourceDispatchPeriod,
      rebuildMode,
      signal: controller.signal,
    }).then((payload) => {
      const contexts = Array.isArray(payload?.contexts) ? payload.contexts.filter(Boolean) : [];
      return contexts[0] || null;
    })))
      .then((contexts) => {
        if (controller.signal.aborted || reassignmentDragRequestIdRef.current !== requestId) {
          return;
        }

        const candidate = buildSuspendedDeliveryPreviewCandidate(contexts.filter(Boolean), candidateOptions);
        if (!candidate?.deliveryId || !Array.isArray(candidate?.compatibilities) || candidate.compatibilities.length === 0) {
          setReassignmentDragState((currentState) => {
            if (!currentState || currentState.requestId !== requestId) {
              return currentState;
            }

            return {
              ...currentState,
              message: hasCrossPeriodTargets
                ? `Déposez la commande suspendue sur un camion compatible du ${sourceDispatchPeriod === "evening" ? "Jour" : "Soir"}.`
                : candidate?.errorMessage || candidate?.message || (isGroupDrag
                  ? "Aucun camion compatible n'est disponible pour toutes les commandes de ce groupe suspendu."
                  : "Aucun camion compatible n'est disponible pour cette commande suspendue."),
              status: hasCrossPeriodTargets ? "ready" : "error",
            };
          });
          return;
        }

        setReassignmentDragState((currentState) => {
          if (!currentState || currentState.requestId !== requestId) {
            return currentState;
          }

          const resolvedDeliveryIds = normalizePreviewDeliveryIds(candidate.deliveryIds);
          const candidateTruckIds = candidate.compatibilities.map((compatibility) => compatibility.truck_id);
          const samePeriodCandidatePatch = sourceDispatchPeriod === "evening"
            ? {
                eveningActionableCompatibilityByTruckId: candidate.actionableCompatibilityByTruckId || {},
                eveningCompatibilityByTruckId: candidate.compatibilityByTruckId,
                eveningCompatibleTruckIds: candidateTruckIds,
              }
            : {
                actionableCompatibilityByTruckId: candidate.actionableCompatibilityByTruckId || {},
                compatibilityByTruckId: candidate.compatibilityByTruckId,
                compatibleTruckIds: candidateTruckIds,
              };
          return {
            ...currentState,
            ...samePeriodCandidatePatch,
            ...(resolvedDeliveryIds.length > 1 ? { deliveryIds: resolvedDeliveryIds } : {}),
            hiddenTruckCount: sourceDispatchPeriod === "day"
              ? timelineRows.reduce(
                  (count, timelineRow) => (candidate.compatibilityByTruckId[timelineRow.assignmentId] ? count : count + 1),
                  0,
                )
              : currentState.hiddenTruckCount,
            message: candidate.message || (isGroupDrag
              ? `${sourceLabel}. Déposez ce groupe sur un camion compatible pour le réactiver.`
              : (item?.deliveryName || item?.title
                  ? `${item.deliveryName || item.title}. Déposez cette commande sur un camion compatible pour la réactiver.`
                  : "Déposez cette commande sur un camion compatible pour la réactiver.")),
            status: "ready",
          };
        });
      })
      .catch((fetchError) => {
        if (controller.signal.aborted || reassignmentDragRequestIdRef.current !== requestId) {
          return;
        }

        setReassignmentDragState((currentState) => {
          if (!currentState || currentState.requestId !== requestId) {
            return currentState;
          }
          if ((localCandidate?.deliveryId || hasCrossPeriodTargets) && currentState.status === "ready") {
            return currentState;
          }

          return {
            ...currentState,
            message: fetchError instanceof Error ? fetchError.message : "Impossible de charger les camions compatibles.",
            status: "error",
          };
        });
      });

    return true;
  }, [assignmentInteractionsRef, closeSegmentActionPopover, dispatchDate, eveningRuntimeRevision, eveningTimelineRows, primaryTimelineDispatchPeriod, runtimeRevision, timelineRows, toggleTruckFilterDropdown]);

  const handleSuspendedDeliveryPointerDown = React.useCallback((event, item = null) => {
    const deliveryIds = resolveSuspendedPreviewDeliveryIds(item);
    const deliveryId = deliveryIds[0] || "";
    if (event.button !== 0 || !event.isPrimary || !deliveryId || item?.isPending) {
      return;
    }

    if (
      event.target instanceof Element
      && event.target.closest("button, a, input, label, textarea, select, [contenteditable='true']")
    ) {
      return;
    }

    clearReassignmentPointerGesture();
    suppressReassignmentClickRef.current = false;

    const gesture = {
      cleanup: null,
      dragIntent: false,
      item,
      lastClientX: event.clientX,
      lastClientY: event.clientY,
      nativeDragActive: false,
      pointerId: event.pointerId,
      previewStarted: false,
      startX: event.clientX,
      startY: event.clientY,
    };

    const markNativeDragHandoff = () => {
      gesture.nativeDragActive = true;
      gesture.dragIntent = true;
    };

    const startPreview = () => {
      if (gesture.previewStarted) {
        return;
      }

      gesture.previewStarted = startSuspendedDeliveryPreview(
        gesture.item,
        { clientX: gesture.lastClientX, clientY: gesture.lastClientY },
      );
      if (!gesture.previewStarted) {
        return;
      }

      suppressReassignmentClickRef.current = true;
      const dropTarget = resolveReassignmentDropTargetFromClientPoint(gesture.lastClientX, gesture.lastClientY);
      setHoveredReassignmentTarget(
        dropTarget.assignmentId,
        {
          clientX: gesture.lastClientX,
          clientY: gesture.lastClientY,
          dispatchPeriod: dropTarget.dispatchPeriod,
          dropZone: dropTarget.dropZone,
        },
      );
    };

    const finishGesture = (shouldCommit, pointerEvent = null) => {
      if (pointerEvent && pointerEvent.pointerId !== gesture.pointerId) {
        return;
      }

      if (shouldCommit && gesture.nativeDragActive) {
        return;
      }

      const clientX = Number.isFinite(Number(pointerEvent?.clientX)) ? pointerEvent.clientX : gesture.lastClientX;
      const clientY = Number.isFinite(Number(pointerEvent?.clientY)) ? pointerEvent.clientY : gesture.lastClientY;
      const hadPreview = gesture.previewStarted;
      clearReassignmentPointerGesture();

      if (!hadPreview) {
        return;
      }

      suppressReassignmentClickRef.current = true;
      finishSuspendedDeliveryDropAtPoint(clientX, clientY, { shouldCommit });
    };

    const handleWindowPointerMove = (moveEvent) => {
      if (moveEvent.pointerId !== gesture.pointerId) {
        return;
      }

      gesture.lastClientX = moveEvent.clientX;
      gesture.lastClientY = moveEvent.clientY;

      if (
        !gesture.dragIntent
        && Math.hypot(moveEvent.clientX - gesture.startX, moveEvent.clientY - gesture.startY) >= REASSIGNMENT_POINTER_MOVE_THRESHOLD_PX
      ) {
        gesture.dragIntent = true;
      }

      if (!gesture.previewStarted) {
        startPreview();
      }

      if (gesture.previewStarted) {
        const dropTarget = resolveReassignmentDropTargetFromClientPoint(moveEvent.clientX, moveEvent.clientY);
        setHoveredReassignmentTarget(
          dropTarget.assignmentId,
          {
            clientX: moveEvent.clientX,
            clientY: moveEvent.clientY,
            dispatchPeriod: dropTarget.dispatchPeriod,
            dropZone: dropTarget.dropZone,
          },
        );
      }
    };

    const handleWindowPointerUp = (upEvent) => {
      finishGesture(true, upEvent);
    };

    const handleWindowPointerCancel = (cancelEvent) => {
      if (cancelEvent.pointerId !== gesture.pointerId) {
        return;
      }

      // Le navigateur annule le pointer quand le drag HTML5 prend le relais.
      markNativeDragHandoff();
    };

    const handleWindowDragStart = () => {
      markNativeDragHandoff();
      if (!gesture.previewStarted) {
        startPreview();
      }
    };

    const handleWindowBlur = () => {
      finishGesture(false);
    };

    const handleWindowKeyDown = (keyEvent) => {
      if (keyEvent.key !== "Escape") {
        return;
      }

      keyEvent.preventDefault();
      finishGesture(false);
    };

    gesture.cleanup = () => {
      window.removeEventListener("pointermove", handleWindowPointerMove, true);
      window.removeEventListener("pointerup", handleWindowPointerUp, true);
      window.removeEventListener("pointercancel", handleWindowPointerCancel, true);
      window.removeEventListener("dragstart", handleWindowDragStart, true);
      window.removeEventListener("blur", handleWindowBlur);
      window.removeEventListener("keydown", handleWindowKeyDown, true);
    };

    startPreview();

    window.addEventListener("pointermove", handleWindowPointerMove, true);
    window.addEventListener("pointerup", handleWindowPointerUp, true);
    window.addEventListener("pointercancel", handleWindowPointerCancel, true);
    window.addEventListener("dragstart", handleWindowDragStart, true);
    window.addEventListener("blur", handleWindowBlur);
    window.addEventListener("keydown", handleWindowKeyDown, true);
    reassignmentPointerGestureRef.current = gesture;
  }, [
    clearReassignmentPointerGesture,
    finishSuspendedDeliveryDropAtPoint,
    setHoveredReassignmentTarget,
    startSuspendedDeliveryPreview,
  ]);

  const handleSuspendedDeliveryDragStart = React.useCallback((event, item = null) => {
    const deliveryId = resolveSuspendedPreviewDeliveryIds(item)[0] || "";
    if (!deliveryId || item?.isPending) {
      event.preventDefault();
      return;
    }

    event.stopPropagation();
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = "move";
      try {
        event.dataTransfer.setData("text/plain", deliveryId);
      } catch {
        event.dataTransfer.setData("text", deliveryId);
      }
    }

    startSuspendedDeliveryPreview(item, { clientX: event.clientX, clientY: event.clientY });
    const activeGesture = reassignmentPointerGestureRef.current;
    if (activeGesture) {
      activeGesture.nativeDragActive = true;
      activeGesture.dragIntent = true;
    }
  }, [startSuspendedDeliveryPreview]);

  const handleSuspendedDeliveryPreview = React.useCallback((item = null) => {
    startSuspendedDeliveryPreview(item);
  }, [startSuspendedDeliveryPreview]);

  const handleReassignmentDragOver = React.useCallback((event, targetAssignmentId, dispatchPeriod = "day") => {
    if (reassignmentDragState?.status !== "ready") {
      return;
    }

    const isEveningTarget = dispatchPeriod === "evening";
    const sourceDispatchPeriod = reassignmentDragState.sourceDispatchPeriod === "evening" ? "evening" : "day";
    const targetDispatchPeriod = isEveningTarget ? "evening" : "day";
    const isCrossDispatchPeriodTarget = sourceDispatchPeriod !== targetDispatchPeriod;
    const compatibility = isEveningTarget
      ? reassignmentDragState.eveningActionableCompatibilityByTruckId?.[targetAssignmentId]
      : reassignmentDragState.actionableCompatibilityByTruckId?.[targetAssignmentId];
    if (
      !compatibility
      || (!isCrossDispatchPeriodTarget && isEquivalentReassignmentTarget(reassignmentDragState, targetAssignmentId, compatibility))
    ) {
      return;
    }

    event.preventDefault();
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = "move";
    }

    setHoveredReassignmentTarget(targetAssignmentId, {
      clientX: event.clientX,
      clientY: event.clientY,
      dispatchPeriod: isEveningTarget ? "evening" : "day",
    });
  }, [reassignmentDragState, setHoveredReassignmentTarget]);

  const handleReassignmentDrop = React.useCallback((event, targetAssignmentId, dispatchPeriod = "day") => {
    if (reassignmentDragState?.status !== "ready") {
      return;
    }

    if (!commitReassignmentToDropTarget({
      assignmentId: targetAssignmentId,
      dispatchPeriod: dispatchPeriod === "evening" ? "evening" : "day",
      dropZone: "truck",
    })) {
      return;
    }

    event.preventDefault();
  }, [commitReassignmentToDropTarget, reassignmentDragState?.status]);

  const handleSuspendedDropZoneDragOver = React.useCallback((event, dispatchPeriod = "") => {
    const targetDispatchPeriod = resolveSuspendedDropZoneDispatchPeriod(event, dispatchPeriod);
    if (!canDropActiveDeliveryOnSuspendedPeriod(reassignmentDragState, targetDispatchPeriod)
      && !canTransferSuspendedDeliveryToPeriod(reassignmentDragState, targetDispatchPeriod)) {
      return;
    }

    event.preventDefault();
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = "move";
    }

    setHoveredReassignmentTarget("", {
      clientX: event.clientX,
      clientY: event.clientY,
      dispatchPeriod: targetDispatchPeriod,
      dropZone: "suspended",
    });
  }, [reassignmentDragState, setHoveredReassignmentTarget]);

  const handleSuspendedDropZoneDrop = React.useCallback((event, dispatchPeriod = "") => {
    const targetDispatchPeriod = resolveSuspendedDropZoneDispatchPeriod(event, dispatchPeriod);
    if (!canDropActiveDeliveryOnSuspendedPeriod(reassignmentDragState, targetDispatchPeriod)
      && !canTransferSuspendedDeliveryToPeriod(reassignmentDragState, targetDispatchPeriod)) {
      return;
    }

    if (!commitDraggedDeliverySuspension(targetDispatchPeriod)) {
      return;
    }

    event.preventDefault();
  }, [
    commitDraggedDeliverySuspension,
    reassignmentDragState?.deliveryId,
    reassignmentDragState?.dragKind,
    reassignmentDragState?.sourceDispatchPeriod,
  ]);

  const disabledTimelineRowCount = React.useMemo(() => {
    if (reassignmentDragState?.status !== "ready") {
      return 0;
    }

    return timelineRows.reduce((count, row) => {
      if (row.assignmentId === reassignmentDragState.sourceAssignmentId) {
        return count;
      }

      return reassignmentDragState.actionableCompatibilityByTruckId?.[row.assignmentId] ? count : count + 1;
    }, 0);
  }, [reassignmentDragState, timelineRows]);

  const dragNoticeState = React.useMemo(() => {
    return resolveFleetReassignmentDragNoticeState(reassignmentDragState, disabledTimelineRowCount);
  }, [disabledTimelineRowCount, reassignmentDragState]);

  return {
    clearReassignmentClickSuppression,
    consumeReassignmentClickSuppression,
    dragNoticeState,
    finishImmediateSegmentReassignment,
    handleReassignmentDragEnd,
    handleReassignmentDragOver,
    handleReassignmentDrop,
    handleSegmentReassignmentDragStart,
    handleSegmentReassignmentPointerDown,
    handleSuspendedDeliveryDragStart,
    handleSuspendedDeliveryPointerDown,
    handleSuspendedDeliveryPreview,
    handleSuspendedDropZoneDragOver,
    handleSuspendedDropZoneDrop,
    handleUndoReassignment,
    reassignmentDragState,
    resetReassignmentDragState,
    startImmediateSegmentReassignment,
    undoReassignmentState,
    updateImmediateSegmentReassignment,
  };
}
